import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Popover,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Container,
  Stack,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Drawer,
  alpha,
  useTheme,
  TextField,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tab,
  Tabs,
  Skeleton
} from '@mui/material';
import { 
  Security as SecurityIcon, 
  PlayArrow, 
  CheckCircle, 
  Warning, 
  Error as ErrorIcon,
  Assessment,
  CloudQueue,
  Info,
  Download,
  FileDownload,
  Build,
  Terminal,
  ContentCopy,
  Shield,
  History,
  Delete as DeleteIcon,
  Refresh,
  AccessTime,
  ExpandMore,
  ExpandLess,
  Description,
  Close
} from '@mui/icons-material';

// Inline SVG icons — avoids MUI version compatibility issues
const AiSparkIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
  </svg>
);
const RadarScanIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>
    <path d="M12 12l4-6"/>
  </svg>
);
import ReactMarkdown from 'react-markdown';
import AWSAccountSetup from '../components/AWSAccountSetup';
import { useNotifications } from '../contexts/NotificationContext';
import { useLayout } from '../contexts/LayoutContext';

function Security() {
  const theme = useTheme();
  const [isScanning, setIsScanning] = useState(false);
  // Per-account scanning state for parallel scans
  const [scanningAccounts, setScanningAccounts] = useState({}); // { accountId: true/false }
  const isAccountScanning = (accountId) => !!scanningAccounts[accountId];
  const setAccountScanning = (accountId, value) => {
    setScanningAccounts(prev => ({ ...prev, [accountId]: value }));
    // Keep isScanning in sync with the currently selected account
    if (accountId === selectedAccount) setIsScanning(value);
  };
  const [scanResults, setScanResults] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [lastScanTimestamp, setLastScanTimestamp] = useState(null);
  const [showScanHistory, setShowScanHistory] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [showDetailedFindings, setShowDetailedFindings] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [drawerCheck, setDrawerCheck] = useState(null);
  const [drawerResource, setDrawerResource] = useState(null);

  // Suppress state
  const [suppressions, setSuppressions] = useState([]);
  const [showSuppressDialog, setShowSuppressDialog] = useState(false);
  const [suppressReason, setSuppressReason] = useState('accepted_risk');
  const [suppressJustification, setSuppressJustification] = useState('');
  const [suppressExpiry, setSuppressExpiry] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Findings, 1 = Suppressed
  const [selectedSuppressions, setSelectedSuppressions] = useState([]);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisGeneratedAt, setAiAnalysisGeneratedAt] = useState(null);
  const [aiAnalysisCached, setAiAnalysisCached] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [aiChatCheckContext, setAiChatCheckContext] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [scanProgress, setScanProgress] = useState(null); // real-time scan progress

  const openCheckDrawer = (group) => {
    setDrawerCheck(group);
    setTopBarHidden(true);
  };

  const closeCheckDrawer = () => {
    setDrawerCheck(null);
    setDrawerResource(null);
    setTopBarHidden(false);
  };

  const openResourceDrawer = (resource, check) => {
    setDrawerResource({ ...resource, check });
  };

  const closeResourceDrawer = () => {
    setDrawerResource(null);
  };
  const [findingsFilter, setFindingsFilter] = useState('all'); // all, high, medium, low, passed
  const [findingsPage, setFindingsPage] = useState(0);
  const findingsPerPage = 50;
  const [pdfUrl, setPdfUrl] = useState('');
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showAWSSetup, setShowAWSSetup] = useState(false);
  const [error, setError] = useState('');
  const [troubleshooting, setTroubleshooting] = useState(null);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugLoading, setDebugLoading] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const { addNotification, updateNotification } = useNotifications();
  const { setTopBarHidden } = useLayout();

  // Scan schedule state
  const [scanSchedule, setScanSchedule] = useState(null);
  const [scheduleFrequency, setScheduleFrequency] = useState('24h');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [dbScanHistory, setDbScanHistory] = useState([]);
  const [customRepeat, setCustomRepeat] = useState('days');
  const [customInterval, setCustomInterval] = useState(2);
  const [customTime, setCustomTime] = useState('02:00');
  const [customDay, setCustomDay] = useState(1);

  // Reset layout when unmounting (navigating away)
  useEffect(() => {
    return () => {
      setTopBarHidden(false);
    };
  }, [setTopBarHidden]);

  const restoreNotifications = () => {
    try {
      const savedNotification = localStorage.getItem('lastScanNotification');
      if (savedNotification) {
        const notificationData = JSON.parse(savedNotification);
        const notificationTime = new Date(notificationData.timestamp);
        const now = new Date();
        const timeDiff = (now - notificationTime) / (1000 * 60); // minutes
        
        // Show notification if it's less than 30 minutes old
        if (timeDiff < 30) {
          addNotification({
            type: notificationData.type,
            title: notificationData.title,
            message: notificationData.message,
            persistent: false
          });
        }
      }
    } catch (error) {
      console.log('Error restoring notifications:', error);
    }
  };

  useEffect(() => {
    loadAWSAccounts();
    restoreNotifications();
  }, []);

  const loadScanHistory = () => {
    // Legacy localStorage history — only used to populate the history dialog list.
    // Do NOT set lastScanTimestamp here; that must come from the DB via loadDbScanHistory.
    try {
      const history = localStorage.getItem('cspm_scan_history');
      if (history) {
        const parsedHistory = JSON.parse(history);
        setScanHistory(parsedHistory);
      }
    } catch (error) {
      console.error('Error loading scan history:', error);
    }
  };

  const saveScanToHistory = (results, accountId) => {
    try {
      const scanEntry = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        accountId: accountId,
        accountName: awsAccounts.find(acc => acc.id === accountId)?.name || 'Unknown',
        summary: {
          totalChecks: results.totalChecks || 0,
          passed: results.passed || 0,
          failed: results.failed || 0,
          critical: results.critical || 0,
          high: results.high || 0,
          medium: results.medium || 0,
          low: results.low || 0,
          score: results.score || 0
        },
        results: results // Store full results
      };

      // Get existing history
      const existingHistory = JSON.parse(localStorage.getItem('cspm_scan_history') || '[]');
      
      // Add new scan at the beginning
      const updatedHistory = [scanEntry, ...existingHistory];
      
      // Keep only last 50 scans
      const trimmedHistory = updatedHistory.slice(0, 50);
      
      // Save to localStorage
      localStorage.setItem('cspm_scan_history', JSON.stringify(trimmedHistory));
      
      // Update state — do NOT call setLastScanTimestamp here; the DB timestamp
      // is the source of truth and is set by loadDbScanHistory on scan completion.
      setScanHistory(trimmedHistory);
    } catch (error) {
      console.error('Error saving scan to history:', error);
    }
  };

  const loadScanFromHistory = async (scanEntry) => {
    // Load findings from DB for this specific scan if possible
    if (scanEntry.accountId && selectedAccount) {
      await loadDbFindings(selectedAccount);
    }
    setLastScanTimestamp(scanEntry.timestamp);
    setSelectedAccount(scanEntry.accountId);
    setShowResults(true);
    setShowScanHistory(false);
    
    addNotification({
      type: 'info',
      title: 'Historical Scan Loaded',
      message: `Viewing scan from ${formatTimestamp(scanEntry.timestamp)}`
    });
  };

  const clearScanHistory = () => {
    if (window.confirm('Are you sure you want to clear all scan history? This cannot be undone.')) {
      localStorage.removeItem('cspm_scan_history');
      setScanHistory([]);
      addNotification({
        type: 'success',
        title: 'History Cleared',
        message: 'All scan history has been cleared'
      });
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    // Future dates
    if (diffMs < 0) {
      const futureMins = Math.floor(-diffMs / 60000);
      const futureHours = Math.floor(-diffMs / 3600000);
      if (futureMins < 1) return 'In less than a minute';
      if (futureMins < 60) return `In ${futureMins} min`;
      if (futureHours < 24) return `In ${futureHours}h ${futureMins % 60}m`;
      return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    }

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFullTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const downloadHistoricalScanPDF = (scan) => {
    try {
      const htmlContent = generateSecurityScanHTML(scan);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);

      addNotification({
        type: 'success',
        title: 'Report Ready',
        message: `Security scan report from ${formatTimestamp(scan.timestamp)} opened. Use browser's Print to PDF.`
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate PDF report'
      });
    }
  };

  const downloadHistoricalScanCSV = (scan) => {
    try {
      const csvContent = generateSecurityScanCSV(scan);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const filename = `security-scan-${scan.accountName.replace(/\s+/g, '-')}-${new Date(scan.timestamp).toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification({
        type: 'success',
        title: 'CSV Downloaded',
        message: `Security scan report from ${formatTimestamp(scan.timestamp)} downloaded successfully`
      });
    } catch (error) {
      console.error('CSV generation failed:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate CSV report'
      });
    }
  };

  const generateSecurityScanHTML = (scan) => {
    // Use current scanResults for findings detail if available, otherwise fall back to scan data
    const findings = scanResults?.allFindings || scan.results?.allFindings || [];
    const failedFindings = findings.filter(f => {
      const s = (f.status || '').toUpperCase();
      return s === 'FAILED' || s === 'FAIL' || s === 'NON-COMPLIANT';
    });
    const passedFindings = findings.filter(f => {
      const s = (f.status || '').toUpperCase();
      return s === 'PASSED' || s === 'PASS' || s === 'COMPLIANT' || s === 'OK';
    });

    const sevColor = (sev) => {
      if (sev === 'CRITICAL') return '#b71c1c';
      if (sev === 'HIGH') return '#d32f2f';
      if (sev === 'MEDIUM') return '#ed6c02';
      if (sev === 'LOW') return '#0288d1';
      return '#4caf50';
    };

    let findingsRows = '';
    failedFindings.forEach((f, i) => {
      const colonIdx = (f.title || '').indexOf(':');
      const checkName = colonIdx > 0 ? f.title.substring(0, colonIdx).trim() : (f.title || 'Unknown');
      const resource = colonIdx > 0 ? f.title.substring(colonIdx + 1).trim() : (f.resourceId || 'N/A');
      findingsRows += `<tr>
        <td>${i + 1}</td>
        <td><span style="color:${sevColor(f.severity)};font-weight:bold">${f.severity || 'N/A'}</span></td>
        <td>${checkName}</td>
        <td style="font-family:monospace;font-size:12px;word-break:break-all">${resource}</td>
        <td>${f.service || f.category || 'N/A'}</td>
        <td>${f.region || 'global'}</td>
        <td style="font-size:12px">${(f.remediation || 'N/A').substring(0, 200)}${(f.remediation || '').length > 200 ? '...' : ''}</td>
      </tr>`;
    });

    let passedRows = '';
    passedFindings.forEach((f, i) => {
      const colonIdx = (f.title || '').indexOf(':');
      const checkName = colonIdx > 0 ? f.title.substring(0, colonIdx).trim() : (f.title || 'Unknown');
      const resource = colonIdx > 0 ? f.title.substring(colonIdx + 1).trim() : (f.resourceId || 'N/A');
      passedRows += `<tr>
        <td>${i + 1}</td>
        <td>${checkName}</td>
        <td style="font-family:monospace;font-size:12px">${resource}</td>
        <td>${f.service || f.category || 'N/A'}</td>
        <td>${f.region || 'global'}</td>
      </tr>`;
    });

    return `<!DOCTYPE html>
<html>
<head>
  <title>CSPM Security Report - ${scan.accountName}</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; margin: 30px; color: #333; }
    h1 { color: #1565c0; border-bottom: 3px solid #1565c0; padding-bottom: 10px; }
    h2 { color: #424242; margin-top: 35px; border-bottom: 1px solid #e0e0e0; padding-bottom: 8px; }
    .meta { color: #666; margin-bottom: 25px; }
    .summary { background: #f5f7fa; padding: 24px; border-radius: 12px; margin: 20px 0; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-top: 16px; }
    .summary-card { background: white; padding: 18px; border-radius: 8px; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.08); }
    .summary-value { font-size: 36px; font-weight: 800; margin: 8px 0; }
    .score { color: ${scan.summary.score >= 80 ? '#4caf50' : scan.summary.score >= 60 ? '#ff9800' : '#f44336'}; }
    .passed { color: #4caf50; }
    .failed { color: #f44336; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 13px; }
    th, td { border: 1px solid #e0e0e0; padding: 10px 12px; text-align: left; }
    th { background: #1565c0; color: white; font-weight: 600; }
    tr:nth-child(even) { background: #fafafa; }
    .sev-critical { background: #ffebee; }
    .sev-high { background: #fff3e0; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e0e0e0; color: #999; font-size: 12px; }
    @media print { .no-print { display: none; } body { margin: 15px; } }
  </style>
</head>
<body>
  <h1>Cloud Security Posture Management Report</h1>
  <div class="meta">
    <p><strong>Account:</strong> ${scan.accountName} &nbsp;|&nbsp; <strong>Scan Date:</strong> ${formatFullTimestamp(scan.timestamp)} &nbsp;|&nbsp; <strong>Scan ID:</strong> ${scan.id}</p>
  </div>

  <div class="summary">
    <div class="summary-grid">
      <div class="summary-card">
        <div>Security Score</div>
        <div class="summary-value score">${scan.summary.score}%</div>
      </div>
      <div class="summary-card">
        <div>Passed</div>
        <div class="summary-value passed">${scan.summary.passed || 0}</div>
      </div>
      <div class="summary-card">
        <div>Failed</div>
        <div class="summary-value failed">${scan.summary.failed || 0}</div>
      </div>
      <div class="summary-card">
        <div>Total Checks</div>
        <div class="summary-value">${scan.summary.totalChecks || 0}</div>
      </div>
    </div>
  </div>

  ${failedFindings.length > 0 ? `
  <h2>Failed Checks (${failedFindings.length})</h2>
  <table>
    <tr>
      <th>#</th>
      <th>Severity</th>
      <th>Check</th>
      <th>Resource</th>
      <th>Service</th>
      <th>Region</th>
      <th>Remediation</th>
    </tr>
    ${findingsRows}
  </table>` : ''}

  ${passedFindings.length > 0 ? `
  <h2>Passed Checks (${passedFindings.length})</h2>
  <table>
    <tr>
      <th>#</th>
      <th>Check</th>
      <th>Resource</th>
      <th>Service</th>
      <th>Region</th>
    </tr>
    ${passedRows}
  </table>` : ''}

  <div class="footer">
    <p>Generated: ${new Date().toLocaleString()} &nbsp;|&nbsp; Cloud Security Posture Management</p>
  </div>

  <p class="no-print" style="text-align:center;margin-top:30px">
    <button onclick="window.print()" style="padding:12px 24px;font-size:15px;cursor:pointer;background:#1565c0;color:white;border:none;border-radius:6px">
      Print / Save as PDF
    </button>
  </p>
</body>
</html>`;
  };

  const generateSecurityScanCSV = (scan) => {
    const allFindings = scan.findings || scanResults?.allFindings || [];
    const failedFindings = allFindings.filter(f => f.status === 'FAILED');
    const passedFindings = allFindings.filter(f => f.status === 'PASSED');

    const escCSV = (val) => {
      const s = String(val || '').replace(/"/g, '""');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
    };

    let csv = `CSPM Security Scan Report\n`;
    csv += `Account,${escCSV(scan.accountName)}\n`;
    csv += `Scan Date,${escCSV(formatFullTimestamp(scan.timestamp))}\n`;
    csv += `Scan ID,${scan.id}\n`;
    csv += `Security Score,${scan.summary.score}%\n`;
    csv += `Total Checks,${scan.summary.totalChecks || 0}\n`;
    csv += `Passed,${scan.summary.passed || 0}\n`;
    csv += `Failed,${scan.summary.failed || 0}\n`;
    csv += `\n`;

    // Failed findings
    if (failedFindings.length > 0) {
      csv += `FAILED CHECKS (${failedFindings.length})\n`;
      csv += `Severity,Status,Check,Resource,Service,Region,Remediation\n`;
      failedFindings.forEach(f => {
        const checkName = f.title ? f.title.split(':')[0].trim() : 'Unknown';
        const resource = f.resourceId || (f.title && f.title.includes(':') ? f.title.split(':').slice(1).join(':').trim() : '-');
        csv += `${escCSV(f.severity)},${escCSV(f.status)},${escCSV(checkName)},${escCSV(resource)},${escCSV(f.service || f.category || '-')},${escCSV(f.region || '-')},${escCSV(f.remediation || '-')}\n`;
      });
      csv += `\n`;
    }

    // Passed findings
    if (passedFindings.length > 0) {
      csv += `PASSED CHECKS (${passedFindings.length})\n`;
      csv += `Status,Check,Resource,Service,Region\n`;
      passedFindings.forEach(f => {
        const checkName = f.title ? f.title.split(':')[0].trim() : 'Unknown';
        const resource = f.resourceId || (f.title && f.title.includes(':') ? f.title.split(':').slice(1).join(':').trim() : '-');
        csv += `PASSED,${escCSV(checkName)},${escCSV(resource)},${escCSV(f.service || f.category || '-')},${escCSV(f.region || '-')}\n`;
      });
    }

    csv += `\nGenerated: ${new Date().toLocaleString()}\n`;
    return csv;
  };

  const checkForRunningScan = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;

      // Support multiple running scans — stored as a JSON object keyed by accountId
      let runningScans = {};
      try {
        const raw = localStorage.getItem('runningScans');
        if (raw) runningScans = JSON.parse(raw);
      } catch (_) {}

      // Legacy single-scan format migration
      const legacyScan = localStorage.getItem('runningScan');
      if (legacyScan) {
        try {
          const parsed = JSON.parse(legacyScan);
          if (parsed.accountId) {
            runningScans[parsed.accountId] = parsed;
          }
          localStorage.removeItem('runningScan');
          localStorage.setItem('runningScans', JSON.stringify(runningScans));
        } catch (_) {}
      }

      const scanData = runningScans[selectedAccount];
      if (!scanData) return false;

      const { accountId, scanId, startTime: scanStartTime } = scanData;
      if (!accountId || accountId !== selectedAccount) return false;

      // Helper to remove this account from running scans
      const clearRunning = () => {
        try {
          const current = JSON.parse(localStorage.getItem('runningScans') || '{}');
          delete current[accountId];
          localStorage.setItem('runningScans', JSON.stringify(current));
        } catch (_) {}
        localStorage.removeItem('scanProgress');
      };

      // Always ask the server — never rely on localStorage age
      if (scanId) {
        const statusRes = await fetch(`/api/security/cspm-scan/${scanId}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (statusRes.ok) {
          const s = await statusRes.json();

          if (s.status === 'completed' || s.status === 'failed') {
            clearRunning();
            if (s.status === 'completed') {
              await loadDbScanHistory(accountId);
              await loadDbFindings(accountId);
              setShowResults(true);
            }
            return false;
          }

          // Still running — show banner and clear stale results
          setAccountScanning(accountId, true);
          setScanResults(null);
          setShowResults(false);
          setAiAnalysis(null);
          setAiAnalysisGeneratedAt(null);
          setAiAnalysisCached(false);
          setScanProgress({
            percent:        s.percent ?? 0,
            checksDone:     s.checksDone ?? 0,
            checksTotal:    s.checksTotal ?? 0,
            currentService: s.currentService ?? '',
            elapsedStr:     s.elapsedStr ?? '',
            remainingSecs:  s.remainingSecs ?? null,
            message:        s.progressMessage ?? 'Scan in progress...'
          });

          const notificationId = addNotification({
            type: 'info',
            title: 'CSPM Scan In Progress',
            message: 'Security scan is still running. Results will load automatically.',
            persistent: true
          });

          // Resume polling
          const POLL_MS = 15000;
          const pollTimer = setInterval(async () => {
            try {
              const pollToken = localStorage.getItem('token');

              const progressRes = await fetch(`/api/security/cspm-scan/${scanId}/status`, {
                headers: { 'Authorization': `Bearer ${pollToken}` }
              });
              if (progressRes.ok) {
                const ps = await progressRes.json();
                setScanProgress({
                  percent:        ps.percent ?? 0,
                  checksDone:     ps.checksDone ?? 0,
                  checksTotal:    ps.checksTotal ?? 0,
                  currentService: ps.currentService ?? '',
                  elapsedStr:     ps.elapsedStr ?? '',
                  remainingSecs:  ps.remainingSecs ?? null,
                  message:        ps.progressMessage ?? ''
                });
              }

              const assessRes = await fetch(`/api/security/assessments/${accountId}`, {
                headers: { 'Authorization': `Bearer ${pollToken}` }
              });
              if (!assessRes.ok) return;

              const statusData = await assessRes.json();
              const latest = statusData.assessments?.[0];

              if (latest && latest.status === 'completed' && new Date(latest.created_at) >= new Date(scanStartTime)) {
                clearInterval(pollTimer);
                clearRunning();
                await loadDbScanHistory(accountId);
                await loadDbFindings(accountId);
                setShowResults(true);
                setAccountScanning(accountId, false);
                setScanProgress(null);
                await loadCachedAiAnalysis(accountId);
                updateNotification(notificationId, {
                  type: 'success',
                  title: 'CSPM Scan Complete',
                  message: 'Security assessment completed successfully.',
                  persistent: false
                });
              } else if (latest && latest.status === 'failed') {
                clearInterval(pollTimer);
                clearRunning();
                setAccountScanning(accountId, false);
                setScanProgress(null);
                try {
                  const errRes = await fetch(`/api/security/assessments/${accountId}/latest-error`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                  });
                  if (errRes.ok) {
                    const errData = await errRes.json();
                    setError(errData.error || 'Scan failed on the server.');
                  } else {
                    setError('Scan failed on the server.');
                  }
                } catch (_) {
                  setError('Scan failed on the server.');
                }
                updateNotification(notificationId, { type: 'error', title: 'CSPM Scan Failed', message: 'The scan failed — see error details on the page.', persistent: false });
              }
            } catch (_) {}
          }, POLL_MS);

          return true; // scan is running
        }
      }

      // No scanId or status endpoint unreachable — fall back to checking assessments
      const assessRes = await fetch(`/api/security/assessments/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (assessRes.ok) {
        const data = await assessRes.json();
        const latest = data.assessments?.[0];
        if (latest && latest.status === 'completed' && new Date(latest.created_at) >= new Date(scanStartTime)) {
          clearRunning();
          await loadDbScanHistory(accountId);
          await loadDbFindings(accountId);
          setShowResults(true);
          return false;
        }
        if (latest && latest.status === 'running') {
          setAccountScanning(accountId, true);
          setScanResults(null);
          setShowResults(false);
          setAiAnalysis(null);
          setAiAnalysisGeneratedAt(null);
          setAiAnalysisCached(false);
          return true; // scan is running
        } else {
          clearRunning();
        }
      }
      return false;
    } catch (error) {
      console.log('Error checking running scan:', error);
      localStorage.removeItem('runningScan');
      return false;
    }
  };

  const loadCompletedScan = async (accountId, assessmentId, token) => {
    try {
      setResultsLoading(true);
      const detailsResponse = await fetch(`/api/security/assessments/${accountId}/${assessmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        if (detailsData.assessment && detailsData.assessment.findings) {
          const findings = detailsData.assessment.findings;
          setScanResults({
            reportId: assessmentId,
            riskScore: detailsData.assessment.risk_score || 0,
            totalChecks: findings.totalChecks || 0,
            passed: findings.passed || 0,
            failed: findings.failed || 0,
            warnings: findings.warnings || 0,
            categories: findings.categories || {},
            criticalFindings: findings.criticalFindings || [],
            allFindings: findings.allFindings || [],
            pdfAvailable: true,
            createdAt: detailsData.assessment.created_at
          });
          setShowResults(true);
        }
      }
    } catch (error) {
      console.log('Error loading completed scan:', error);
    } finally {
      setResultsLoading(false);
    }
  };

  const checkForRecentScans = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Get list of AWS accounts first
      const accountsResponse = await fetch('/api/aws/accounts', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        if (accountsData.accounts.length > 0) {
          const firstAccount = accountsData.accounts[0];
          
          // Check for recent assessments
          const assessmentsResponse = await fetch(`/api/security/assessments/${firstAccount.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (assessmentsResponse.ok) {
            const assessmentsData = await assessmentsResponse.json();
            if (assessmentsData.assessments && assessmentsData.assessments.length > 0) {
              const latestAssessment = assessmentsData.assessments[0];
              
              // If there's a recent completed assessment, load its details
              if (latestAssessment.status === 'completed') {
                const detailsResponse = await fetch(`/api/security/assessments/${firstAccount.id}/${latestAssessment.id}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });

                if (detailsResponse.ok) {
                  const detailsData = await detailsResponse.json();
                  if (detailsData.assessment && detailsData.assessment.findings) {
                    // Convert the stored findings to the expected format
                    const findings = detailsData.assessment.findings;
                    setScanResults({
                      reportId: latestAssessment.id,
                      riskScore: latestAssessment.risk_score || 0,
                      totalChecks: findings.totalChecks || 0,
                      passed: findings.passed || 0,
                      failed: findings.failed || 0,
                      warnings: findings.warnings || 0,
                      categories: findings.categories || {},
                      criticalFindings: findings.criticalFindings || [],
                      allFindings: findings.allFindings || [], // Include all findings
                      pdfAvailable: true,
                      createdAt: latestAssessment.created_at
                    });
                    setShowResults(true);
                    setSelectedAccount(firstAccount.id);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.log('No recent scans found or error checking:', error);
    }
  };

  const loadAWSAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/aws/accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAwsAccounts(data.accounts);
        if (data.accounts.length > 0) {
          setSelectedAccount(data.accounts[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load AWS accounts:', err);
    }
  };

  // ── Suppress functions ──
  const loadSuppressions = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !accountId) return;
      const response = await fetch(`/api/security/suppressions/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuppressions(data.suppressions || []);
      }
    } catch (err) {
      console.error('Failed to load suppressions:', err);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      const accountCurrentlyScanning = !!scanningAccounts[selectedAccount];

      // Clear previous account's state immediately before loading new account data
      setScanResults(null);
      setShowResults(false);
      setResultsLoading(true);
      setAiAnalysis(null);
      setAiAnalysisGeneratedAt(null);
      setAiAnalysisCached(false);
      setLastScanTimestamp(null);
      setError('');
      setFindingsFilter('all');
      setFindingsPage(0);
      // Sync isScanning with the newly selected account
      setIsScanning(accountCurrentlyScanning);

      // Run all data loading sequentially to avoid race conditions.
      // checkForRunningScan must run FIRST — if it detects a running scan it will
      // set accountScanning and clear results, preventing stale data from showing.
      const loadAccountData = async () => {
        loadSuppressions(selectedAccount);
        loadScanSchedule(selectedAccount);

        // Check for running scan FIRST — this may set isScanning and clear results
        const scanIsRunning = await checkForRunningScan();

        if (!accountCurrentlyScanning && !scanIsRunning) {
          // No scan running — load history (sets timestamp), then findings, then AI
          await loadDbScanHistory(selectedAccount);
          await loadDbFindings(selectedAccount);
          await loadCachedAiAnalysis(selectedAccount);
        } else {
          // Scanning — only load history for the dialog, don't show results
          await loadDbScanHistory(selectedAccount);
        }
      };
      loadAccountData();
    }
  }, [selectedAccount]);

  // ── Load findings from DB (instant) ──
  const loadDbFindings = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !accountId) return;
      setResultsLoading(true);
      const response = await fetch(`/api/security/cspm-findings/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.findings) {
          setScanResults(data.findings);
          setShowResults(true);
          // Do NOT set lastScanTimestamp here — loadDbScanHistory is the single
          // source of truth for the timestamp to avoid race-condition overwrites.
        }
      }
    } catch (err) {
      console.error('Failed to load DB findings:', err);
    } finally {
      setResultsLoading(false);
    }
  };

  // ── Load scan history from DB ──
  const loadDbScanHistory = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !accountId) return;
      const response = await fetch(`/api/security/cspm-history/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDbScanHistory(data.history || []);
        // Also update legacy scanHistory for the history dialog
        const legacyFormat = (data.history || []).map(h => ({
          id: h.id,
          timestamp: h.completed_at || h.started_at,
          accountId: accountId,
          accountName: awsAccounts.find(a => a.id == accountId)?.account_name || 'AWS Account',
          summary: {
            totalChecks: h.total_checks || 0,
            passed: h.passed || 0,
            failed: h.failed || 0,
            critical: 0,
            high: 0,
            medium: 0,
            low: 0,
            score: h.risk_score || 0
          },
          scanType: h.scan_type,
          status: h.status,
          durationMs: h.duration_ms
        }));
        setScanHistory(legacyFormat);
        // Always use the most recent scan's timestamp from history
        if (legacyFormat.length > 0) {
          setLastScanTimestamp(legacyFormat[0].timestamp);
        }
      }
    } catch (err) {
      console.error('Failed to load DB scan history:', err);
    }
  };

  // ── Load scan schedule from DB ──
  const loadScanSchedule = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !accountId) return;
      const response = await fetch(`/api/security/cspm-schedule/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.schedule) {
          setScanSchedule(data.schedule);
          // Migrate old hourly frequencies to daily
          const freq = data.schedule.frequency || '24h';
          const migratedFreq = ['1h', '6h', '12h'].includes(freq) ? '24h' : freq;
          setScheduleFrequency(migratedFreq);
          setScheduleEnabled(data.schedule.enabled);
        }
      }
    } catch (err) {
      console.error('Failed to load scan schedule:', err);
    }
  };

  // ── Update scan schedule ──
  const handleScheduleUpdate = async (frequency, enabled, options = {}) => {
    if (!selectedAccount) return;
    setScheduleLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/cspm-schedule/${selectedAccount}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ frequency, enabled, ...options })
      });
      if (response.ok) {
        const data = await response.json();
        setScanSchedule(data.schedule);
        setScheduleFrequency(data.schedule.frequency);
        setScheduleEnabled(data.schedule.enabled);
        const freqLabels = { '1h': 'hour', '6h': '6 hours', '12h': '12 hours', '24h': '24 hours', 'weekly': 'week', 'monthly': 'month' };
        addNotification({
          type: 'success',
          title: 'Schedule Updated',
          message: enabled ? `Automated scans set to every ${freqLabels[frequency] || frequency}` : 'Automated scans disabled'
        });
      } else {
        addNotification({ type: 'error', title: 'Schedule Error', message: 'Failed to update scan schedule' });
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Schedule Error', message: 'Network error' });
    } finally {
      setScheduleLoading(false);
    }
  };

  const isFindingSuppressed = (checkType) => {
    return suppressions.some(s => s.check_type === checkType && (!s.expires_at || new Date(s.expires_at) > new Date()));
  };

  const handleSuppressSubmit = async () => {
    if (!drawerCheck || !selectedAccount) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/suppressions/${selectedAccount}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          findingKey: drawerCheck.checkType,
          checkType: drawerCheck.checkType,
          resourceId: drawerCheck.resources?.[0]?.resourceId || null,
          severity: drawerCheck.severity || null,
          service: drawerCheck.service || null,
          reason: suppressReason,
          justification: suppressJustification || null,
          expiresAt: suppressExpiry || null
        })
      });
      if (response.ok) {
        addNotification({ type: 'success', title: 'Finding Suppressed', message: `"${drawerCheck.checkType}" has been suppressed` });
        setShowSuppressDialog(false);
        setSuppressReason('accepted_risk');
        setSuppressJustification('');
        setSuppressExpiry('');
        closeCheckDrawer();
        await loadSuppressions(selectedAccount);
      } else {
        const err = await response.json();
        addNotification({ type: 'error', title: 'Suppress Failed', message: err.error || 'Failed to suppress finding' });
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Suppress Failed', message: 'Network error' });
    }
  };

  const handleUnsuppress = async (suppressionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/suppressions/${selectedAccount}/${suppressionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        addNotification({ type: 'success', title: 'Finding Reactivated', message: 'Finding is now active again' });
        await loadSuppressions(selectedAccount);
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to reactivate finding' });
    }
  };

  const handleBulkUnsuppress = async () => {
    if (selectedSuppressions.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/suppressions/${selectedAccount}/bulk-unsuppress`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ suppressionIds: selectedSuppressions })
      });
      if (response.ok) {
        const data = await response.json();
        addNotification({ type: 'success', title: 'Findings Reactivated', message: `${data.suppressions.length} findings reactivated` });
        setSelectedSuppressions([]);
        await loadSuppressions(selectedAccount);
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to bulk reactivate' });
    }
  };

  const reasonLabels = {
    accepted_risk: 'Accepted Risk',
    false_positive: 'False Positive',
    compensating_control: 'Compensating Control',
    not_applicable: 'Not Applicable'
  };

  // ── AI Analysis functions ──
  const loadCachedAiAnalysis = async (accountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !accountId) return;
      const response = await fetch(`/api/security/ai-analysis/${accountId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.analysis && data.cached) {
          // Only show cached AI analysis if it was generated AFTER the latest scan.
          // This prevents stale analysis from a previous scan appearing for a new account view.
          const analysisTime = data.generatedAt ? new Date(data.generatedAt) : null;
          const scanTime = data.latestScanAt ? new Date(data.latestScanAt) : null;

          if (analysisTime && scanTime && analysisTime >= scanTime) {
            setAiAnalysis(data.analysis);
            setAiAnalysisGeneratedAt(data.generatedAt);
            setAiAnalysisCached(true);
          } else if (analysisTime && !scanTime) {
            // No scan time info from server — show it (legacy fallback)
            setAiAnalysis(data.analysis);
            setAiAnalysisGeneratedAt(data.generatedAt);
            setAiAnalysisCached(true);
          }
          // else: analysis is older than latest scan — don't show stale analysis
        }
      }
    } catch (err) {
      console.error('Failed to load cached AI analysis:', err);
    }
  };

  const handleAiAnalyze = async (forceRegenerate = false) => {
    if (!selectedAccount || !scanResults) return;
    setAiAnalysisLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/ai-analyze/${selectedAccount}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanResults })
      });
      if (response.ok) {
        const data = await response.json();
        setAiAnalysis(data.analysis);
        setAiAnalysisGeneratedAt(data.generatedAt);
        setAiAnalysisCached(false);
        addNotification({ type: 'success', title: 'AI Analysis Complete', message: 'Security posture analysis generated' });
      } else {
        const err = await response.json();
        addNotification({ type: 'error', title: 'AI Analysis Failed', message: err.error || 'Failed to generate analysis' });
      }
    } catch (err) {
      addNotification({ type: 'error', title: 'AI Analysis Error', message: 'Network error' });
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleAiChatSend = async () => {
    if (!aiChatInput.trim() || aiChatLoading) return;
    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiChatLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/security/ai-chat/${selectedAccount}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory: aiChatMessages,
          scanResults,
          analysisContext: aiAnalysis,
          checkContext: aiChatCheckContext
        })
      });
      if (response.ok) {
        const data = await response.json();
        setAiChatMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      } else {
        setAiChatMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
      }
    } catch (err) {
      setAiChatMessages(prev => [...prev, { role: 'assistant', content: 'Network error. Please check your connection.' }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  const openAiChatForCheck = (check) => {
    const ctx = {
      checkType: check.checkType,
      severity: check.severity,
      status: check.status,
      service: check.service,
      description: check.description,
      remediation: check.remediation,
      downtime: check.downtime,
      downtimeDetail: check.downtimeDetail,
      resourceCount: check.resources?.length || 0,
      resources: (check.resources || []).slice(0, 10).map(r => ({ resourceId: r.resourceId, region: r.region }))
    };
    setAiChatCheckContext(ctx);
    setAiChatMessages([]);
    setAiChatInput('');
    setShowAiChat(true);
  };

  const openAiChatGlobal = () => {
    setAiChatCheckContext(null);
    setAiChatMessages([]);
    setAiChatInput('');
    setShowAiChat(true);
  };

  const handleStartScan = async () => {
    if (!selectedAccount) {
      setError('Please select an AWS account first');
      return;
    }

    const scanAccountId = selectedAccount; // capture at scan start for parallel safety
    setAccountScanning(scanAccountId, true);
    setError('');
    setTroubleshooting(null);
    // Clear previous results and AI analysis — show clean slate during scan
    setScanResults(null);
    setShowResults(false);
    setAiAnalysis(null);
    setAiAnalysisGeneratedAt(null);
    setAiAnalysisCached(false);

    const scanStartTime = new Date().toISOString();
    // Store in multi-account running scans map
    try {
      const runningScans = JSON.parse(localStorage.getItem('runningScans') || '{}');
      runningScans[selectedAccount] = { accountId: selectedAccount, scanId: null, startTime: scanStartTime };
      localStorage.setItem('runningScans', JSON.stringify(runningScans));
    } catch (_) {}
    localStorage.removeItem('scanProgress');

    const notificationId = addNotification({
      type: 'info',
      title: 'CSPM Scan Started',
      message: 'Running comprehensive security assessment...',
      persistent: true
    });

    let progressInterval;
    let currentProgress = 0;
    const updateProgress = () => {
      currentProgress += Math.random() * 10;
      if (currentProgress > 90) currentProgress = 90;
      localStorage.setItem('scanProgress', currentProgress.toString());
      updateNotification(notificationId, {
        type: 'info',
        title: 'CSPM Scan In Progress',
        message: `Security assessment running... ${Math.round(currentProgress)}% complete`,
        persistent: true
      });
    };
    progressInterval = setInterval(updateProgress, 3000);

    try {
      const token = localStorage.getItem('token');

      // Step 1: Initiate the scan (returns immediately with scanId)
      const initiateResponse = await fetch(`/api/security/cspm-scan/${selectedAccount}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!initiateResponse.ok) {
        clearInterval(progressInterval);
        const errorData = await initiateResponse.json();

        if (initiateResponse.status === 409) {
          setError('');
          // Remove this account from running scans
          try {
            const rs = JSON.parse(localStorage.getItem('runningScans') || '{}');
            delete rs[scanAccountId];
            localStorage.setItem('runningScans', JSON.stringify(rs));
          } catch (_) {}
          localStorage.removeItem('scanProgress');
          updateNotification(notificationId, {
            type: 'warning',
            title: 'Scan Already Running',
            message: errorData.message || 'A scan is already in progress. Please wait.',
            persistent: false
          });
          setAccountScanning(scanAccountId, false);
          return;
        }

        setError(errorData.error || 'Failed to start scan');
        if (errorData.troubleshooting) setTroubleshooting(errorData.troubleshooting);
        try {
          const rs = JSON.parse(localStorage.getItem('runningScans') || '{}');
          delete rs[scanAccountId];
          localStorage.setItem('runningScans', JSON.stringify(rs));
        } catch (_) {}
        localStorage.removeItem('scanProgress');
        updateNotification(notificationId, {
          type: 'error',
          title: 'CSPM Scan Failed',
          message: errorData.error || 'Scan failed',
          persistent: false
        });
        setAccountScanning(scanAccountId, false);
        return;
      }

      const initiateData = await initiateResponse.json();
      const scanId = initiateData.scan?.scanId;

      // Persist scanId so new tabs / page refreshes can resume polling the real status
      try {
        const runningScans = JSON.parse(localStorage.getItem('runningScans') || '{}');
        runningScans[selectedAccount] = { accountId: selectedAccount, scanId: scanId || null, startTime: scanStartTime };
        localStorage.setItem('runningScans', JSON.stringify(runningScans));
      } catch (_) {}

      // Scan is now running on the server — it can take up to 2 hours for large accounts.
      // Poll the status endpoint every 15s for real scan progress (checks done, %, service).
      clearInterval(progressInterval);
      setIsScanning(true);
      setScanProgress({ percent: 0, checksDone: 0, checksTotal: 0, currentService: '', elapsedStr: '', remainingSecs: null, message: 'Security scan queued...' });

      updateNotification(notificationId, {
        type: 'info',
        title: 'CSPM Scan Running',
        message: 'Scanning your AWS account. Results load automatically when done.',
        persistent: true
      });

      // Background poll — no max attempts, no timeout
      const POLL_MS = 15000; // 15 seconds
      const pollTimer = setInterval(async () => {
        try {
          const pollToken = localStorage.getItem('token');

          // 1. Get real-time progress from status endpoint (if we have a scanId)
          if (scanId) {
            const statusRes = await fetch(`/api/security/cspm-scan/${scanId}/status`, {
              headers: { 'Authorization': `Bearer ${pollToken}` }
            });
            if (statusRes.ok) {
              const s = await statusRes.json();
              setScanProgress({
                percent:        s.percent ?? 0,
                checksDone:     s.checksDone ?? 0,
                checksTotal:    s.checksTotal ?? 0,
                currentService: s.currentService ?? '',
                elapsedStr:     s.elapsedStr ?? '',
                remainingSecs:  s.remainingSecs ?? null,
                message:        s.progressMessage ?? ''
              });
            }
          }

          // 2. Check if the assessment record is completed
          const assessRes = await fetch(`/api/security/assessments/${selectedAccount}`, {
            headers: { 'Authorization': `Bearer ${pollToken}` }
          });
          if (!assessRes.ok) return;

          const statusData = await assessRes.json();
          const latest = statusData.assessments?.[0];

          if (latest && latest.status === 'completed' && new Date(latest.created_at) >= new Date(scanStartTime)) {
            clearInterval(pollTimer);
            try {
              const rs = JSON.parse(localStorage.getItem('runningScans') || '{}');
              delete rs[scanAccountId];
              localStorage.setItem('runningScans', JSON.stringify(rs));
            } catch (_) {}
            localStorage.removeItem('scanProgress');

            await loadDbScanHistory(scanAccountId);
            await loadDbFindings(scanAccountId);
            // lastScanTimestamp is set by loadDbScanHistory — do NOT override with new Date() here
            setShowResults(true);
            setAccountScanning(scanAccountId, false);
            setScanProgress(null);
            // Load AI analysis now that scan is done (respects the timestamp guard)
            await loadCachedAiAnalysis(scanAccountId);

            updateNotification(notificationId, {
              type: 'success',
              title: 'CSPM Scan Complete',
              message: 'Security assessment completed successfully.',
              persistent: false
            });

            localStorage.setItem('lastScanNotification', JSON.stringify({
              type: 'success',
              title: 'CSPM Scan Complete',
              message: 'Security assessment completed successfully.',
              timestamp: new Date().toISOString()
            }));
          } else if (latest && latest.status === 'failed') {
            clearInterval(pollTimer);
            try {
              const rs = JSON.parse(localStorage.getItem('runningScans') || '{}');
              delete rs[scanAccountId];
              localStorage.setItem('runningScans', JSON.stringify(rs));
            } catch (_) {}
            localStorage.removeItem('scanProgress');
            setAccountScanning(scanAccountId, false);
            setScanProgress(null);
            // Fetch the actual error message from the DB
            try {
              const errRes = await fetch(`/api/security/assessments/${scanAccountId}/latest-error`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
              if (errRes.ok) {
                const errData = await errRes.json();
                setError(errData.error || 'Scan failed on the server.');
              } else {
                setError('Scan failed on the server.');
              }
            } catch (_) {
              setError('Scan failed on the server.');
            }
            updateNotification(notificationId, {
              type: 'error',
              title: 'CSPM Scan Failed',
              message: 'The scan failed — see error details on the page.',
              persistent: false
            });
          }
        } catch (_) {
          // Network hiccup — keep polling silently
        }
      }, POLL_MS);

    } catch (err) {
      clearInterval(progressInterval);
      try {
        const rs = JSON.parse(localStorage.getItem('runningScans') || '{}');
        delete rs[scanAccountId];
        localStorage.setItem('runningScans', JSON.stringify(rs));
      } catch (_) {}
      localStorage.removeItem('scanProgress');
      setAccountScanning(scanAccountId, false);
      setScanProgress(null);
      setError(err.message || 'Network error. Please try again.');
      updateNotification(notificationId, {
        type: 'error',
        title: 'CSPM Scan Error',
        message: err.message || 'Network error occurred',
        persistent: false
      });
    }
  };

  // ... rest of the component remains the same
  // I'll just add the key functions that are needed

  const handleAccountAdded = (newAccount) => {
    setAwsAccounts(prev => [...prev, newAccount]);
    setSelectedAccount(newAccount.id);
  };

  const handleDebugCheck = async () => {
    setDebugLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/security/debug/prowler-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDebugInfo(data);
        setShowDebugInfo(true);
      } else {
        addNotification({
          type: 'error',
          title: 'Debug Check Failed',
          message: 'Unable to run system diagnostics'
        });
      }
    } catch (err) {
      addNotification({
        type: 'error',
        title: 'Debug Check Error',
        message: 'Network error during system check'
      });
    } finally {
      setDebugLoading(false);
    }
  };

  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoAnchorEl(null);
  };

  const infoOpen = Boolean(infoAnchorEl);

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {/* Modern Hero Section */}
      <Box sx={{ mb: 5 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(211, 47, 47, 0.25)'
                  }}
                >
                  <Shield sx={{ fontSize: 28, color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
                    Cloud Security Posture Management
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Continuous security monitoring and compliance validation for AWS infrastructure
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Tooltip title="Learn about CSPM">
                <IconButton 
                  onClick={handleInfoClick}
                  size="large"
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': { 
                      bgcolor: alpha(theme.palette.primary.main, 0.12)
                    }
                  }}
                >
                  <Info color="primary" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Error Display with Troubleshooting */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 6, 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: 'error.50'
          }}
        >
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {troubleshooting && (
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setShowTroubleshooting(true)}
                  startIcon={<Build />}
                  sx={{ borderRadius: 2 }}
                >
                  View Troubleshooting Steps
                </Button>
              )}
              <Button 
                variant="outlined" 
                size="small" 
                onClick={handleDebugCheck}
                disabled={debugLoading}
                startIcon={debugLoading ? <CircularProgress size={16} /> : <Terminal />}
                sx={{ borderRadius: 2 }}
              >
                {debugLoading ? 'Checking...' : 'Run System Check'}
              </Button>
            </Stack>
          </Stack>
        </Alert>
      )}

      {/* Troubleshooting Dialog */}
      <Dialog 
        open={showTroubleshooting} 
        onClose={() => setShowTroubleshooting(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Build color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {troubleshooting?.title || 'Troubleshooting Steps'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {troubleshooting && (
            <Stepper orientation="vertical">
              {troubleshooting.steps.map((step, index) => (
                <Step key={index} active={true}>
                  <StepLabel>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" paragraph>
                      {step.description}
                    </Typography>
                    
                    {step.command && (
                      <Paper sx={{ p: 2, bgcolor: 'grey.100', mb: 2 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Terminal fontSize="small" />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                            {step.command}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => navigator.clipboard.writeText(step.command)}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    )}
                    
                    {step.note && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          <strong>Note:</strong> {step.note}
                        </Typography>
                      </Alert>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowTroubleshooting(false)}>Close</Button>
          <Button 
            variant="contained" 
            onClick={handleStartScan}
            startIcon={<PlayArrow />}
          >
            Retry Scan
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSPM Information Popover */}
      <Popover
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }
        }}
      >
        <Box sx={{ p: 4, maxWidth: 400 }}>
          <Stack spacing={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                <RadarScanIcon size={20} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                What is CSPM?
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              Cloud Security Posture Management (CSPM) continuously monitors your AWS infrastructure for security misconfigurations, compliance violations, and potential threats.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              It helps maintain a strong security posture by identifying vulnerabilities before they can be exploited and provides actionable remediation steps.
            </Typography>
            <Box sx={{ pt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label="AI-Enhanced Detection" 
                size="small" 
                sx={{ fontWeight: 600, bgcolor: '#2563eb', color: 'white', fontSize: '0.75rem' }}
              />
              <Chip 
                label="Real-time Monitoring" 
                size="small" 
                sx={{ fontWeight: 600, bgcolor: '#7c3aed', color: 'white', fontSize: '0.75rem' }}
              />
              <Chip 
                label="Automated Remediation" 
                size="small" 
                sx={{ fontWeight: 600, bgcolor: '#16a34a', color: 'white', fontSize: '0.75rem' }}
              />
            </Box>
          </Stack>
        </Box>
      </Popover>

      {/* Modern Scan Control Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* AWS Account Selection Card */}
        <Grid item xs={12} lg={8}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: theme.shadows[4]
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CloudQueue color="primary" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      AWS Account
                    </Typography>
                  </Box>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowAWSSetup(true)}
                    sx={{ 
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    + Add Account
                  </Button>
                </Box>
                
                {awsAccounts.length > 0 ? (
                  <FormControl fullWidth size="medium">
                    <InputLabel>Select AWS Account</InputLabel>
                    <Select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      label="Select AWS Account"
                      sx={{ borderRadius: 2 }}
                    >
                      {awsAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          <Box display="flex" alignItems="center" gap={2} width="100%">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                              {account.account_name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {account.account_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {account.account_id}
                              </Typography>
                            </Box>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Alert 
                    severity="info" 
                    sx={{ borderRadius: 2 }}
                    action={
                      <Button 
                        color="primary" 
                        size="small" 
                        onClick={() => setShowAWSSetup(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Now
                      </Button>
                    }
                  >
                    <Typography variant="body2">
                      No AWS accounts configured. Add an account to begin security assessment.
                    </Typography>
                  </Alert>
                )}

                {/* Automated Scans — inline under account selector */}
                {selectedAccount && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.grey[50], 0.7),
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          sx={{
                            width: 36, height: 36, borderRadius: 1.5,
                            bgcolor: scheduleEnabled ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.grey[500], 0.08),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <AccessTime sx={{ fontSize: 18, color: scheduleEnabled ? 'primary.main' : 'text.secondary' }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            Automated Scans
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scheduleEnabled
                              ? `Running ${scheduleFrequency === '24h' ? 'daily' : scheduleFrequency === 'weekly' ? `weekly (${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][scanSchedule?.day_of_week || 1]})` : scheduleFrequency === 'monthly' ? `monthly (day ${scanSchedule?.day_of_month || 1})` : scheduleFrequency === 'custom' ? `every ${customInterval} ${customRepeat} at ${customTime}` : scheduleFrequency}`
                              : 'Disabled — enable for continuous monitoring'}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={2} alignItems="center">
                        {scanSchedule?.last_scan_at && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            Last: {formatTimestamp(scanSchedule.last_scan_at)}
                          </Typography>
                        )}
                        {scanSchedule?.next_scan_at && scheduleEnabled && (
                          <Chip
                            label={`Next: ${formatTimestamp(scanSchedule.next_scan_at)}`}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                          />
                        )}
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <Select
                            value={scheduleFrequency}
                            onChange={(e) => {
                              setScheduleFrequency(e.target.value);
                              if (scheduleEnabled) handleScheduleUpdate(e.target.value, true);
                            }}
                            disabled={scheduleLoading}
                            sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                          >
                            <MenuItem value="24h">Daily</MenuItem>
                            <MenuItem value="weekly">Weekly</MenuItem>
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                          </Select>
                        </FormControl>
                        {scheduleFrequency === 'weekly' && (
                          <FormControl size="small" sx={{ minWidth: 80 }}>
                            <Select
                              value={scanSchedule?.day_of_week ?? 1}
                              onChange={(e) => {
                                if (scheduleEnabled) handleScheduleUpdate('weekly', true, { dayOfWeek: e.target.value, scanTime: scanSchedule?.scan_time || '02:00' });
                              }}
                              disabled={scheduleLoading}
                              sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                            >
                              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                                <MenuItem key={i} value={i}>{d}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {scheduleFrequency === 'monthly' && (
                          <FormControl size="small" sx={{ minWidth: 70 }}>
                            <Select
                              value={scanSchedule?.day_of_month ?? 1}
                              onChange={(e) => {
                                if (scheduleEnabled) handleScheduleUpdate('monthly', true, { dayOfMonth: e.target.value, scanTime: scanSchedule?.scan_time || '02:00' });
                              }}
                              disabled={scheduleLoading}
                              sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                            >
                              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                <MenuItem key={d} value={d}>Day {d}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {scheduleFrequency === 'custom' && (
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Every</Typography>
                            <TextField
                              size="small"
                              type="number"
                              value={customInterval}
                              onChange={(e) => setCustomInterval(Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
                              disabled={scheduleLoading}
                              inputProps={{ min: 1, max: 99 }}
                              sx={{ width: 56, '& .MuiInputBase-root': { borderRadius: 2, fontSize: '0.8rem', height: 34 } }}
                            />
                            <FormControl size="small" sx={{ minWidth: 85 }}>
                              <Select
                                value={customRepeat}
                                onChange={(e) => setCustomRepeat(e.target.value)}
                                disabled={scheduleLoading}
                                sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                              >
                                <MenuItem value="days">Day(s)</MenuItem>
                                <MenuItem value="weeks">Week(s)</MenuItem>
                                <MenuItem value="months">Month(s)</MenuItem>
                              </Select>
                            </FormControl>
                            {customRepeat === 'weeks' && (
                              <FormControl size="small" sx={{ minWidth: 70 }}>
                                <Select
                                  value={customDay}
                                  onChange={(e) => setCustomDay(e.target.value)}
                                  disabled={scheduleLoading}
                                  sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                                >
                                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
                                    <MenuItem key={i} value={i}>{d}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            {customRepeat === 'months' && (
                              <FormControl size="small" sx={{ minWidth: 70 }}>
                                <Select
                                  value={customDay}
                                  onChange={(e) => setCustomDay(e.target.value)}
                                  disabled={scheduleLoading}
                                  sx={{ borderRadius: 2, fontSize: '0.8rem', height: 34 }}
                                >
                                  {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                                    <MenuItem key={d} value={d}>Day {d}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>at</Typography>
                            <TextField
                              size="small"
                              type="time"
                              value={customTime}
                              onChange={(e) => setCustomTime(e.target.value)}
                              disabled={scheduleLoading}
                              sx={{ width: 100, '& .MuiInputBase-root': { borderRadius: 2, fontSize: '0.8rem', height: 34 } }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={scheduleLoading}
                              onClick={() => {
                                const [hh, mm] = (customTime || '02:00').split(':').map(Number);
                                let cron = '';
                                if (customRepeat === 'days') {
                                  cron = `${mm} ${hh} */${customInterval} * *`;
                                } else if (customRepeat === 'weeks') {
                                  cron = `${mm} ${hh} */${customInterval * 7} * ${customDay}`;
                                } else if (customRepeat === 'months') {
                                  cron = `${mm} ${hh} ${customDay} */${customInterval} *`;
                                }
                                handleScheduleUpdate('custom', true, { cronExpression: cron, scanTime: customTime });
                              }}
                              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', minWidth: 56, height: 34 }}
                            >
                              Apply
                            </Button>
                          </Stack>
                        )}
                        <Button
                          variant={scheduleEnabled ? 'contained' : 'outlined'}
                          size="small"
                          disabled={scheduleLoading}
                          onClick={() => handleScheduleUpdate(scheduleFrequency, !scheduleEnabled)}
                          startIcon={scheduleLoading ? <CircularProgress size={14} /> : null}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            minWidth: 85,
                            ...(scheduleEnabled ? {
                              bgcolor: 'success.main',
                              '&:hover': { bgcolor: 'success.dark' }
                            } : {})
                          }}
                        >
                          {scheduleLoading ? '' : scheduleEnabled ? 'Enabled' : 'Enable'}
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Scan Action Card */}
        <Grid item xs={12} lg={4}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.2s ease'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      background: isScanning 
                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {isScanning ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      <PlayArrow sx={{ fontSize: 20, color: 'white' }} />
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {isScanning ? 'Scanning...' : 'Security Scan'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {isScanning ? 'Analysis in progress' : 'Run CSPM assessment'}
                    </Typography>
                  </Box>
                </Box>
                
                <Divider />
                
                <Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {isScanning 
                        ? ''
                        : 'Comprehensive security posture assessment using enterprise-grade security checks.'}
                    </Typography>
                  </Box>

                  {/* Inline progress when scanning */}
                  {isScanning && (
                    <Box sx={{ mb: 2 }}>
                      {scanProgress && scanProgress.checksTotal > 0 ? (
                        <>
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                              {scanProgress.currentService
                                ? `Scanning ${scanProgress.currentService.toUpperCase()}`
                                : 'Scanning...'}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {scanProgress.checksDone}/{scanProgress.checksTotal} [{scanProgress.percent}%]
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={scanProgress.percent}
                            sx={{ height: 6, borderRadius: 4, mb: 1 }}
                          />
                          <Box display="flex" gap={2} flexWrap="wrap">
                            {scanProgress.elapsedStr && (
                              <Typography variant="caption" color="text.secondary">
                                Elapsed: <strong>{scanProgress.elapsedStr}</strong>
                              </Typography>
                            )}
                            {scanProgress.remainingSecs != null && (
                              <Typography variant="caption" color="text.secondary">
                                ~{Math.ceil(scanProgress.remainingSecs / 60)} min left
                              </Typography>
                            )}
                          </Box>
                        </>
                      ) : (
                        <>
                          <LinearProgress variant="indeterminate" sx={{ height: 4, borderRadius: 4, mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">
                            Scanning your AWS account across all regions. This typically takes 20–40 minutes.
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  
                  {lastScanTimestamp && !isScanning && (
                    <Box 
                      sx={{ 
                        mb: 2, 
                        p: 1.5, 
                        bgcolor: 'success.50', 
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: 'success.200'
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark' }}>
                            Last Scan: {formatTimestamp(lastScanTimestamp)}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {formatFullTimestamp(lastScanTimestamp)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                  
                  <Stack direction="row" spacing={1.5}>
                    <Button
                      variant="contained"
                      size="medium"
                      fullWidth
                      onClick={handleStartScan}
                      disabled={isScanning || !selectedAccount}
                      startIcon={isScanning ? null : <PlayArrow />}
                      sx={{ 
                        borderRadius: 2,
                        py: 1.25,
                        fontSize: '0.9375rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: theme.shadows[2]
                        }
                      }}
                    >
                      {isScanning ? 'Scanning...' : 'Start Scan'}
                    </Button>
                    
                    {scanHistory.length > 0 && (
                      <Tooltip title="View Scan History">
                        <IconButton
                          onClick={() => setShowScanHistory(true)}
                          sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2
                          }}
                        >
                          <Badge badgeContent={scanHistory.length} color="primary" max={99}>
                            <History />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Scan Results — Skeleton Loading */}
      {resultsLoading && !scanResults && (
        <Card sx={{ 
          borderRadius: 4, 
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          mb: 4
        }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={4}>
              {/* Header skeleton */}
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Skeleton variant="text" width={280} height={36} />
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5 }}>
                    <Skeleton variant="rounded" width={180} height={24} sx={{ borderRadius: 3 }} />
                    <Skeleton variant="text" width={120} height={20} />
                  </Stack>
                </Box>
                <Skeleton variant="rounded" width={140} height={36} sx={{ borderRadius: 2 }} />
              </Box>

              {/* Score + posture skeleton */}
              <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    <Skeleton variant="circular" width={72} height={72} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width={200} height={28} />
                      <Skeleton variant="text" width={260} height={18} sx={{ mb: 1.5 }} />
                      <Skeleton variant="rounded" width="100%" height={14} sx={{ borderRadius: 2 }} />
                      <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                        <Skeleton variant="text" width={90} height={16} />
                        <Skeleton variant="text" width={80} height={16} />
                        <Skeleton variant="text" width={60} height={16} />
                        <Skeleton variant="text" width={80} height={16} />
                      </Stack>
                    </Box>
                  </Box>

                  {/* Severity cards skeleton */}
                  <Grid container spacing={1.5}>
                    {[0, 1, 2, 3].map(i => (
                      <Grid item xs={6} sm={3} key={i}>
                        <Box sx={{ p: 1.5, borderRadius: 2, border: `1.5px solid ${theme.palette.divider}` }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Skeleton variant="circular" width={20} height={20} />
                            <Skeleton variant="rounded" width={36} height={18} sx={{ borderRadius: 2 }} />
                          </Box>
                          <Skeleton variant="text" width={40} height={36} />
                          <Skeleton variant="text" width={80} height={18} />
                          <Skeleton variant="text" width={60} height={14} />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Scan Results */}
      {showResults && scanResults && (
        <Card sx={{ 
          borderRadius: 4, 
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          mb: 4
        }}>
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={4}>
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Security Assessment Results
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    {/* Account badge — always visible so user knows which account */}
                    {selectedAccount && (() => {
                      const acc = awsAccounts.find(a => a.id === selectedAccount || a.id == selectedAccount);
                      return acc ? (
                        <Chip
                          avatar={<Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: 'primary.main' }}>{acc.account_name.charAt(0).toUpperCase()}</Avatar>}
                          label={`${acc.account_name} · ${acc.account_id}`}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.75rem', bgcolor: alpha(theme.palette.primary.main, 0.08), color: 'primary.main', height: 24 }}
                        />
                      ) : null;
                    })()}
                    {lastScanTimestamp && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {formatTimestamp(lastScanTimestamp)}
                        </Typography>
                      </Box>
                    )}
                    {scanResults.createdAt && (
                      <Typography variant="caption" color="text.disabled">
                        ({formatFullTimestamp(scanResults.createdAt || lastScanTimestamp)})
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <Stack direction="row" spacing={1.5}>
                  <Box sx={{ position: 'relative' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                          borderColor: 'primary.dark',
                          bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                      }}
                    >
                      Download Report
                    </Button>
                    <Popover
                      open={Boolean(downloadAnchorEl)}
                      anchorEl={downloadAnchorEl}
                      onClose={() => setDownloadAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      PaperProps={{ sx: { borderRadius: 2, mt: 0.5, minWidth: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } }}
                    >
                      {/* Show which account the report is for */}
                      {selectedAccount && (() => {
                        const acc = awsAccounts.find(a => a.id === selectedAccount || a.id == selectedAccount);
                        return acc ? (
                          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                              Exporting report for:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {acc.account_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{acc.account_id}</Typography>
                          </Box>
                        ) : null;
                      })()}
                      <List dense sx={{ py: 0.5 }}>
                        <ListItem disablePadding>
                          <ListItemButton
                            onClick={() => {
                              setDownloadAnchorEl(null);
                              const token = localStorage.getItem('token');
                              const acc = awsAccounts.find(a => a.id === selectedAccount || a.id == selectedAccount);
                              const accSlug = (acc?.account_name || 'account').replace(/\s+/g, '-');
                              const dateStr = new Date().toISOString().split('T')[0];
                              fetch(`/api/security/reports/${selectedAccount}/${scanResults.scanId || scanResults.reportId}/xlsx`, {
                                headers: { 'Authorization': `Bearer ${token}` }
                              })
                              .then(r => r.ok ? r.blob() : r.json().then(e => Promise.reject(e.error || 'Download failed')))
                              .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `cspm-${accSlug}-${dateStr}.xlsx`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                                addNotification({ type: 'success', title: 'Downloaded', message: `Security report for ${acc?.account_name} downloaded` });
                              })
                              .catch(err => addNotification({ type: 'error', title: 'Download Failed', message: typeof err === 'string' ? err : 'Unable to download report' }));
                            }}
                            sx={{ px: 2, py: 1 }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}><FileDownload sx={{ fontSize: 18, color: '#1D6F42' }} /></ListItemIcon>
                            <ListItemText primary="Excel (XLSX)" secondary="Full security assessment report" primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                          </ListItemButton>
                        </ListItem>
                      </List>
                    </Popover>
                  </Box>
                </Stack>
              </Box>

              {/* Security Assessment — single-line overview */}
              <Card 
                elevation={0}
                sx={{ mb: 4, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  {(() => {
                    const total = scanResults.totalChecks || 1;
                    // Count only FAILED findings by severity — matches the Excel report
                    const failedFindings = scanResults.allFindings?.filter(f => f.status === 'FAILED') || [];
                    const critHigh = failedFindings.filter(f => f.severity === 'HIGH' || f.severity === 'CRITICAL').length;
                    const medium = failedFindings.filter(f => f.severity === 'MEDIUM').length;
                    const lowInfo = failedFindings.filter(f => f.severity === 'LOW').length;
                    const passed = scanResults.passed || 0;
                    const scoreColor = scanResults.riskScore >= 80 ? theme.palette.success.main : scanResults.riskScore >= 60 ? theme.palette.warning.main : theme.palette.error.main;

                    return (
                      <>
                        {/* Row 1: Score + posture + stacked bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                          {/* Score circle (compact) */}
                          <Box sx={{ flexShrink: 0 }}>
                            <Box sx={{
                              width: 72, height: 72, borderRadius: '50%',
                              background: `conic-gradient(${scoreColor} ${scanResults.riskScore * 3.6}deg, ${alpha(theme.palette.grey[300], 0.2)} 0deg)`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <Box sx={{ width: 58, height: 58, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', lineHeight: 1 }}>{scanResults.riskScore}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontWeight: 600 }}>/ 100</Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* Posture text + stacked severity bar */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25, lineHeight: 1.2 }}>Overall Security Posture</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                              {scanResults.riskScore >= 80 ? '✓ Excellent - Follows security best practices' :
                               scanResults.riskScore >= 60 ? '⚠ Good - Some improvements recommended' :
                               scanResults.riskScore >= 40 ? '⚠ Fair - Multiple security risks detected' :
                               '✗ Critical - Immediate action required'}
                            </Typography>

                            {/* Stacked horizontal bar */}
                            <Box sx={{ display: 'flex', height: 14, borderRadius: 2, overflow: 'hidden', bgcolor: alpha(theme.palette.grey[300], 0.2) }}>
                              {critHigh > 0 && <Box sx={{ width: `${(critHigh / total) * 100}%`, bgcolor: theme.palette.error.main, transition: 'width 0.3s' }} />}
                              {medium > 0 && <Box sx={{ width: `${(medium / total) * 100}%`, bgcolor: theme.palette.warning.main, transition: 'width 0.3s' }} />}
                              {lowInfo > 0 && <Box sx={{ width: `${(lowInfo / total) * 100}%`, bgcolor: theme.palette.info.main, transition: 'width 0.3s' }} />}
                              {passed > 0 && <Box sx={{ width: `${(passed / total) * 100}%`, bgcolor: theme.palette.success.main, transition: 'width 0.3s' }} />}
                            </Box>

                            {/* Legend */}
                            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                              {[
                                { label: `Critical/High (${critHigh})`, color: theme.palette.error.main, filter: 'high' },
                                { label: `Medium (${medium})`, color: theme.palette.warning.main, filter: 'medium' },
                                { label: `Low (${lowInfo})`, color: theme.palette.info.main, filter: 'low' },
                                { label: `Passed (${passed})`, color: theme.palette.success.main, filter: 'passed' },
                              ].map(item => (
                                <Box
                                  key={item.filter}
                                  onClick={() => { setFindingsFilter(item.filter); setFindingsPage(0); }}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                >
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{item.label}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>

                        {/* Row 2: Severity summary cards */}
                        <Grid container spacing={1.5}>
                          {[
                            { key: 'high', color: theme.palette.error.main, icon: <ErrorIcon sx={{ fontSize: 20, color: 'error.main' }} />, count: critHigh, label: 'Critical & High', sub: 'Immediate attention' },
                            { key: 'medium', color: theme.palette.warning.main, icon: <Warning sx={{ fontSize: 20, color: 'warning.main' }} />, count: medium, label: 'Medium', sub: 'Address soon' },
                            { key: 'low', color: theme.palette.info.main, icon: <Info sx={{ fontSize: 20, color: 'info.main' }} />, count: lowInfo, label: 'Low', sub: 'Minor items' },
                            { key: 'passed', color: theme.palette.success.main, icon: <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />, count: passed, label: 'Passed', sub: 'Best practices' },
                          ].map(card => (
                            <Grid item xs={6} sm={3} key={card.key}>
                              <Box
                                onClick={() => { setFindingsFilter(card.key); setFindingsPage(0); }}
                                sx={{
                                  p: 1.5, borderRadius: 2, cursor: 'pointer',
                                  border: `1.5px solid ${card.color}`,
                                  bgcolor: alpha(card.color, 0.03),
                                  transition: 'all 0.15s ease',
                                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${alpha(card.color, 0.15)}` }
                                }}
                              >
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
                                  {card.icon}
                                  <Chip label="View" size="small" sx={{ bgcolor: card.color, color: 'white', fontWeight: 600, fontSize: '0.55rem', height: 18 }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: card.color, lineHeight: 1 }}>{card.count}</Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 0.25, fontSize: '0.75rem' }}>{card.label}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{card.sub}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Interactive Findings Viewer — always shown */}
              <Card sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 4 }}>
                  {/* Tabs: Findings / Suppressed */}
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 40 }}>
                      <Tab label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>Findings</span>
                          {scanResults.allFindings && (() => {
                            // Count only FAILED findings (excluding suppressed) to match severity cards
                            const failedCount = scanResults.allFindings.filter(f => {
                              const status = (f.status || '').toUpperCase();
                              const isFailed = status === 'FAILED' || status === 'FAIL' || status === 'NON-COMPLIANT';
                              if (!isFailed) return false;
                              const ci = (f.title||'').indexOf(':');
                              const checkType = ci > 0 ? f.title.substring(0,ci).trim() : (f.title||'Unknown');
                              return !isFindingSuppressed(checkType);
                            }).length;
                            return <Chip label={failedCount} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />;
                          })()}
                        </Box>
                      } sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.875rem' }} />
                      <Tab label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>Suppressed</span>
                          <Chip label={suppressions.length} size="small" color={suppressions.length > 0 ? 'warning' : 'default'} sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                      } sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, fontSize: '0.875rem' }} />
                    </Tabs>
                  </Box>

                  {/* Tab 0: Active Findings */}
                  {activeTab === 0 && (
                  <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {findingsFilter !== 'all' && (
                        <Chip 
                          label={
                            findingsFilter === 'high' ? 'Critical & High' :
                            findingsFilter === 'medium' ? 'Medium' :
                            findingsFilter === 'low' ? 'Low' :
                            findingsFilter === 'passed' ? 'Passed' :
                            findingsFilter.charAt(0).toUpperCase() + findingsFilter.slice(1)
                          } 
                          size="small" 
                          sx={{ 
                            mr: 1, 
                            fontWeight: 600,
                            bgcolor: findingsFilter === 'high' ? 'error.main' :
                                     findingsFilter === 'medium' ? 'warning.main' :
                                     findingsFilter === 'low' ? 'info.main' :
                                     findingsFilter === 'passed' ? 'success.main' : 'primary.main',
                            color: '#fff',
                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }
                          }}
                          onDelete={() => { setFindingsFilter('all'); setFindingsPage(0); }}
                        />
                      )}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Filter</InputLabel>
                      <Select
                        value={findingsFilter}
                        onChange={(e) => { setFindingsFilter(e.target.value); setFindingsPage(0); }}
                        label="Filter"
                      >
                        <MenuItem value="all">All Findings</MenuItem>
                        <MenuItem value="high">Critical & High</MenuItem>
                        <MenuItem value="medium">Medium Severity</MenuItem>
                        <MenuItem value="low">Low Severity</MenuItem>
                        <MenuItem value="passed">Passed Checks</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Findings List - Flat list matching Excel report */}
                  <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                    {scanResults.allFindings && (() => {
                      // Filter findings
                      const filtered = scanResults.allFindings.filter(finding => {
                        // Exclude suppressed findings from active view
                        const colonIdx = (finding.title || '').indexOf(':');
                        const checkType = colonIdx > 0 ? finding.title.substring(0, colonIdx).trim() : (finding.title || 'Unknown Check');
                        if (isFindingSuppressed(checkType)) return false;

                        if (findingsFilter === 'all') return true;
                        if (findingsFilter === 'high') {
                          const status = (finding.status || '').toUpperCase();
                          const isFailed = status === 'FAILED' || status === 'FAIL' || status === 'NON-COMPLIANT';
                          return isFailed && (finding.severity === 'HIGH' || finding.severity === 'CRITICAL');
                        }
                        if (findingsFilter === 'medium') {
                          const status = (finding.status || '').toUpperCase();
                          const isFailed = status === 'FAILED' || status === 'FAIL' || status === 'NON-COMPLIANT';
                          return isFailed && finding.severity === 'MEDIUM';
                        }
                        if (findingsFilter === 'low') {
                          const status = (finding.status || '').toUpperCase();
                          const isFailed = status === 'FAILED' || status === 'FAIL' || status === 'NON-COMPLIANT';
                          return isFailed && finding.severity === 'LOW';
                        }
                        if (findingsFilter === 'passed') {
                          const status = (finding.status || '').toUpperCase();
                          return status === 'PASS' || status === 'PASSED' || status === 'COMPLIANT' || status === 'OK';
                        }
                        return false;
                      });

                      if (filtered.length === 0 && findingsFilter === 'passed') {
                        return (
                          <Box sx={{ textAlign: 'center', py: 6 }}>
                            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {scanResults.passed || 0} Checks Passed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Passed checks indicate areas compliant with best practices.
                            </Typography>
                          </Box>
                        );
                      }

                      if (filtered.length === 0) {
                        return (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                              No findings available for the selected filter.
                            </Typography>
                          </Box>
                        );
                      }

                      return (
                        <>
                          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Showing {Math.min(findingsPage * findingsPerPage + 1, filtered.length)}–{Math.min((findingsPage + 1) * findingsPerPage, filtered.length)} of {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Page {findingsPage + 1} of {Math.ceil(filtered.length / findingsPerPage)}
                            </Typography>
                          </Box>
                          {filtered.slice(findingsPage * findingsPerPage, (findingsPage + 1) * findingsPerPage).map((finding, idx) => {
                            const colonIdx = (finding.title || '').indexOf(':');
                            const checkType = colonIdx > 0 ? finding.title.substring(0, colonIdx).trim() : (finding.title || 'Unknown Check');
                            const resourceFromTitle = colonIdx > 0 ? finding.title.substring(colonIdx + 1).trim() : null;
                            const resourceId = finding.resourceId || resourceFromTitle || 'N/A';
                            const isPassed = ['PASS', 'PASSED', 'COMPLIANT', 'OK'].includes((finding.status || '').toUpperCase());

                            const severityColor =
                              finding.severity === 'CRITICAL' ? 'critical' :
                              finding.severity === 'HIGH' ? 'error' :
                              finding.severity === 'MEDIUM' ? 'warning' :
                              finding.severity === 'LOW' ? 'info' :
                              finding.severity === 'INFO' || isPassed ? 'success' : 'default';

                            const severityBgMap = {
                              critical: { bgcolor: '#b71c1c', color: '#fff' },
                              error: { bgcolor: '#d32f2f', color: '#fff' },
                              warning: { bgcolor: '#e65100', color: '#fff' },
                              info: { bgcolor: '#0288d1', color: '#fff' },
                              success: { bgcolor: '#2e7d32', color: '#fff' },
                              default: { bgcolor: '#757575', color: '#fff' }
                            };

                            return (
                              <Box
                                key={finding.id || idx}
                                onClick={() => {
                                  // Build a group-like object for the drawer
                                  openCheckDrawer({
                                    checkType,
                                    severity: finding.severity,
                                    status: finding.status,
                                    description: finding.description,
                                    remediation: finding.remediation,
                                    downtime: finding.downtime || 'unknown',
                                    downtimeDetail: finding.downtimeDetail || null,
                                    service: finding.service,
                                    category: finding.category,
                                    complianceFrameworks: finding.complianceFrameworks,
                                    resources: [{ resourceId, region: finding.region || 'global', status: finding.status }]
                                  });
                                }}
                                sx={{
                                  p: 1.5, mb: 1, borderRadius: 2,
                                  border: '1px solid', borderColor: 'divider',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s ease',
                                  '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                                }}
                              >
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Chip
                                    label={finding.severity || (isPassed ? 'PASSED' : 'N/A')}
                                    size="small"
                                    sx={{
                                      fontWeight: 700, fontSize: '0.65rem', height: 22, minWidth: 70,
                                      ...severityBgMap[severityColor]
                                    }}
                                  />
                                  <Chip
                                    label={(finding.service || finding.category || 'GENERAL').toUpperCase()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: '0.65rem', height: 22 }}
                                  />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }} noWrap>
                                      {checkType}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
                                      {resourceId} · {finding.region || 'global'}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ color: 'text.secondary', fontSize: 18 }}>›</Box>
                                </Box>
                              </Box>
                            );
                          })}
                          {/* Pagination Controls */}
                          {filtered.length > findingsPerPage && (
                            <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={findingsPage === 0}
                                onClick={() => setFindingsPage(0)}
                                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 36 }}
                              >
                                First
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={findingsPage === 0}
                                onClick={() => setFindingsPage(prev => prev - 1)}
                                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 36 }}
                              >
                                Prev
                              </Button>
                              {/* Page numbers */}
                              {(() => {
                                const totalPages = Math.ceil(filtered.length / findingsPerPage);
                                const pages = [];
                                let start = Math.max(0, findingsPage - 2);
                                let end = Math.min(totalPages - 1, findingsPage + 2);
                                // Ensure we always show 5 pages if available
                                if (end - start < 4) {
                                  if (start === 0) end = Math.min(totalPages - 1, start + 4);
                                  else start = Math.max(0, end - 4);
                                }
                                if (start > 0) {
                                  pages.push(
                                    <Button key={0} size="small" variant="outlined" onClick={() => setFindingsPage(0)}
                                      sx={{ borderRadius: 2, minWidth: 32, fontSize: '0.75rem' }}>1</Button>
                                  );
                                  if (start > 1) pages.push(<Typography key="dots-start" variant="caption" sx={{ px: 0.5 }}>...</Typography>);
                                }
                                for (let i = start; i <= end; i++) {
                                  pages.push(
                                    <Button
                                      key={i}
                                      size="small"
                                      variant={i === findingsPage ? 'contained' : 'outlined'}
                                      onClick={() => setFindingsPage(i)}
                                      sx={{ borderRadius: 2, minWidth: 32, fontSize: '0.75rem' }}
                                    >
                                      {i + 1}
                                    </Button>
                                  );
                                }
                                if (end < totalPages - 1) {
                                  if (end < totalPages - 2) pages.push(<Typography key="dots-end" variant="caption" sx={{ px: 0.5 }}>...</Typography>);
                                  pages.push(
                                    <Button key={totalPages - 1} size="small" variant="outlined" onClick={() => setFindingsPage(totalPages - 1)}
                                      sx={{ borderRadius: 2, minWidth: 32, fontSize: '0.75rem' }}>{totalPages}</Button>
                                  );
                                }
                                return pages;
                              })()}
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={findingsPage >= Math.ceil(filtered.length / findingsPerPage) - 1}
                                onClick={() => setFindingsPage(prev => prev + 1)}
                                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 36 }}
                              >
                                Next
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={findingsPage >= Math.ceil(filtered.length / findingsPerPage) - 1}
                                onClick={() => setFindingsPage(Math.ceil(filtered.length / findingsPerPage) - 1)}
                                sx={{ borderRadius: 2, textTransform: 'none', minWidth: 36 }}
                              >
                                Last
                              </Button>
                            </Box>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                  </>
                  )}

                  {/* Tab 1: Suppressed Findings */}
                  {activeTab === 1 && (
                    <Box>
                      {/* Bulk actions header */}
                      {selectedSuppressions.length > 0 && (
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.warning.main, 0.06), borderRadius: 2, border: '1px solid', borderColor: alpha(theme.palette.warning.main, 0.2) }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectedSuppressions.length} selected
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={handleBulkUnsuppress}
                            startIcon={<Refresh sx={{ fontSize: 16 }} />}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                          >
                            Reactivate Selected
                          </Button>
                        </Box>
                      )}

                      {suppressions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                          <Shield sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                            No Suppressed Findings
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            Findings you suppress will appear here. You can reactivate them at any time.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                          {suppressions.map((sup) => {
                            const isExpired = sup.expires_at && new Date(sup.expires_at) < new Date();
                            const isSelected = selectedSuppressions.includes(sup.id);
                            return (
                              <Card
                                key={sup.id}
                                sx={{
                                  mb: 1.5,
                                  border: '1px solid',
                                  borderColor: isSelected ? 'warning.main' : 'divider',
                                  borderRadius: 2.5,
                                  overflow: 'hidden',
                                  transition: 'all 0.15s ease',
                                  opacity: isExpired ? 0.6 : 1,
                                  '&:hover': { borderColor: 'warning.light', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }
                                }}
                              >
                                <Box sx={{ p: 2 }}>
                                  <Box display="flex" alignItems="flex-start" gap={1.5}>
                                    <Checkbox
                                      size="small"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedSuppressions(prev => [...prev, sup.id]);
                                        } else {
                                          setSelectedSuppressions(prev => prev.filter(id => id !== sup.id));
                                        }
                                      }}
                                      sx={{ mt: -0.5, ml: -0.5 }}
                                    />
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                                        <Chip
                                          label={sup.severity || 'N/A'}
                                          size="small"
                                          sx={{
                                            fontWeight: 600,
                                            minWidth: 60,
                                            bgcolor: sup.severity === 'HIGH' || sup.severity === 'CRITICAL' ? 'error.main'
                                              : sup.severity === 'MEDIUM' ? 'warning.main'
                                              : sup.severity === 'LOW' ? 'info.main' : 'grey.500',
                                            color: 'white'
                                          }}
                                        />
                                        <Chip
                                          label={reasonLabels[sup.reason] || sup.reason}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                                        />
                                        {isExpired && (
                                          <Chip label="Expired" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                        )}
                                      </Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                        {sup.check_type}
                                      </Typography>
                                      {sup.justification && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}>
                                          "{sup.justification}"
                                        </Typography>
                                      )}
                                      <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
                                        <Typography variant="caption" color="text.secondary">
                                          Suppressed {formatTimestamp(sup.created_at)}
                                        </Typography>
                                        {sup.suppressed_by_email && (
                                          <Typography variant="caption" color="text.secondary">
                                            by {sup.suppressed_by_email}
                                          </Typography>
                                        )}
                                        {sup.expires_at && (
                                          <Typography variant="caption" color={isExpired ? 'error.main' : 'text.secondary'}>
                                            {isExpired ? 'Expired' : 'Expires'} {formatTimestamp(sup.expires_at)}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                    <Tooltip title="Reactivate finding">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleUnsuppress(sup.id)}
                                        sx={{
                                          color: 'warning.main',
                                          border: '1px solid',
                                          borderColor: alpha(theme.palette.warning.main, 0.3),
                                          borderRadius: 1.5,
                                          '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.08) }
                                        }}
                                      >
                                        <Refresh sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Card>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}

                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Check Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerCheck)}
        onClose={() => closeCheckDrawer()}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '70%' },
            borderRadius: '16px 0 0 16px',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.08)'
          }
        }}
      >
        {drawerCheck && (() => {
          const dc = drawerCheck;
          const isPassed = dc.status && ['PASS', 'PASSED', 'COMPLIANT', 'OK'].includes(dc.status.toUpperCase());
          const severityColor =
            dc.severity === 'CRITICAL' ? 'critical' :
            dc.severity === 'HIGH' ? 'error' :
            dc.severity === 'MEDIUM' ? 'warning' :
            dc.severity === 'LOW' ? 'info' :
            dc.severity === 'INFO' || isPassed ? 'success' : 'default';

          // Determine chip label: show severity for failed, "PASSED" for passed
          const chipLabel = isPassed ? 'PASSED' : (dc.severity || dc.status || 'LOW');

          const drawerSeverityBgMap = {
            critical: { bgcolor: '#b71c1c', color: '#fff' },
            error: { bgcolor: '#d32f2f', color: '#fff' },
            warning: { bgcolor: '#e65100', color: '#fff' },
            info: { bgcolor: '#0288d1', color: '#fff' },
            success: { bgcolor: '#2e7d32', color: '#fff' },
            default: { bgcolor: '#757575', color: '#fff' }
          };
          const chipSx = isPassed ? drawerSeverityBgMap.success : (drawerSeverityBgMap[severityColor] || drawerSeverityBgMap.default);

          // Parse remediation into steps if it contains "Step N:" pattern
          const parseRemediationSteps = (text) => {
            if (!text) return null;
            const stepRegex = /Step\s+\d+:\s*/gi;
            if (!stepRegex.test(text)) return null;
            // Split on "Step N:" and filter empty
            const parts = text.split(/Step\s+\d+:\s*/i).filter(s => s.trim());
            return parts.length > 1 ? parts.map(s => s.trim().replace(/\.\s*$/, '.')) : null;
          };

          const remediationSteps = parseRemediationSteps(dc.remediation);
          const isNoAction = dc.remediation && dc.remediation.toLowerCase().startsWith('no action required');

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1.5 }}>
                      <Chip
                        label={chipLabel}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          ...chipSx
                        }}
                      />
                      {dc.service && (
                        <Chip label={dc.service} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                      {dc.checkType}
                    </Typography>
                  </Box>
                  <IconButton onClick={() => closeCheckDrawer()} size="small" sx={{ mt: -0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Scrollable Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Description */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.7, color: 'text.primary' }}>
                    {dc.description}
                  </Typography>
                </Box>

                {/* Remediation */}
                {dc.remediation && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                      Remediation
                    </Typography>

                    {/* "No action required" style for passed checks */}
                    {isNoAction ? (
                      <Box sx={{
                        mt: 1, p: 2, borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.06),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.success.main, 0.15),
                        display: 'flex', alignItems: 'center', gap: 1.5,
                      }}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke={theme.palette.success.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="4 10 8 14 16 6" />
                        </svg>
                        <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 500 }}>
                          {dc.remediation}
                        </Typography>
                      </Box>
                    ) : remediationSteps ? (
                      /* Numbered step-by-step list */
                      <Box sx={{
                        mt: 1, p: 0, borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                        overflow: 'hidden',
                      }}>
                        {remediationSteps.map((step, sIdx) => (
                          <Box
                            key={sIdx}
                            sx={{
                              display: 'flex', gap: 1.5, px: 2.5, py: 1.5,
                              borderBottom: sIdx < remediationSteps.length - 1 ? '1px solid' : 'none',
                              borderColor: alpha(theme.palette.primary.main, 0.08),
                              alignItems: 'flex-start',
                            }}
                          >
                            <Box sx={{
                              width: 22, height: 22, borderRadius: '50%', flexShrink: 0, mt: '1px',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.7rem', fontWeight: 700,
                            }}>
                              {sIdx + 1}
                            </Box>
                            <Typography variant="body2" sx={{ lineHeight: 1.6, flex: 1 }}>
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      /* Plain text fallback */
                      <Box sx={{
                        mt: 1, p: 2.5, borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        border: '1px solid',
                        borderColor: alpha(theme.palette.primary.main, 0.1),
                        borderLeft: '3px solid',
                        borderLeftColor: 'primary.main'
                      }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {dc.remediation}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Downtime Impact */}
                {dc.downtime && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                      Downtime Impact
                    </Typography>
                    <Box sx={{
                      mt: 1,
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      bgcolor: dc.downtime === 'none'
                        ? alpha(theme.palette.success.main, 0.06)
                        : dc.downtime === 'possible'
                          ? alpha(theme.palette.warning.main, 0.08)
                          : alpha(theme.palette.grey[500], 0.06),
                      border: '1px solid',
                      borderColor: dc.downtime === 'none'
                        ? alpha(theme.palette.success.main, 0.2)
                        : dc.downtime === 'possible'
                          ? alpha(theme.palette.warning.main, 0.3)
                          : alpha(theme.palette.grey[500], 0.15),
                    }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: dc.downtime === 'none'
                          ? alpha(theme.palette.success.main, 0.12)
                          : dc.downtime === 'possible'
                            ? alpha(theme.palette.warning.main, 0.15)
                            : alpha(theme.palette.grey[500], 0.12),
                      }}>
                        {dc.downtime === 'none' ? (
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke={theme.palette.success.main} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="4 10 8 14 16 6" />
                          </svg>
                        ) : dc.downtime === 'possible' ? (
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke={theme.palette.warning.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M10 4v6M10 14v.01" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke={theme.palette.text.secondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="10" cy="10" r="7" />
                            <path d="M10 7v3l2 2" />
                          </svg>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{
                          fontWeight: 600,
                          color: dc.downtime === 'none'
                            ? 'success.dark'
                            : dc.downtime === 'possible'
                              ? 'warning.dark'
                              : 'text.secondary',
                        }}>
                          {dc.downtime === 'none' ? 'No downtime expected'
                            : dc.downtime === 'possible' ? 'Downtime possible'
                            : 'Downtime impact unknown'}
                        </Typography>
                        {dc.downtime === 'none' && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>
                            This remediation can be applied safely without affecting running services.
                          </Typography>
                        )}
                        {dc.downtime === 'possible' && dc.downtimeDetail && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>
                            {dc.downtimeDetail}
                          </Typography>
                        )}
                        {dc.downtime === 'possible' && !dc.downtimeDetail && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>
                            Applying this fix may briefly disrupt connected services. Review the remediation steps and schedule during a maintenance window.
                          </Typography>
                        )}
                        {dc.downtime === 'unknown' && (
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block', mt: 0.25 }}>
                            Downtime impact could not be determined automatically. Review the remediation steps and test in a non-production environment first.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Compliance Frameworks */}
                {dc.complianceFrameworks && dc.complianceFrameworks.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                      Compliance Frameworks
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {dc.complianceFrameworks.map((fw, idx) => (
                        <Chip key={idx} label={fw} size="small" variant="outlined" color="secondary" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Affected Resources */}
                <Box>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                    Affected Resources ({dc.resources.length})
                  </Typography>
                  <Box sx={{ mt: 1.5 }}>
                    {dc.resources.map((resource, rIdx) => (
                      <Box
                        key={rIdx}
                        onClick={() => openResourceDrawer(resource, dc)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 1.5,
                          px: 2,
                          mb: 1,
                          bgcolor: alpha(theme.palette.grey[500], 0.04),
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          '&:hover': { 
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            borderColor: alpha(theme.palette.primary.main, 0.3)
                          }
                        }}
                      >
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%',
                          bgcolor: isPassed ? '#2e7d32' : (drawerSeverityBgMap[severityColor] || drawerSeverityBgMap.default).bgcolor,
                          flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1, fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                          {resource.resourceId}
                        </Typography>
                        <Chip label={resource.region} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', flexShrink: 0 }} />
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke={theme.palette.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <polyline points="8 5 13 10 8 15" />
                        </svg>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Drawer Footer — AI + Suppress */}
              <Box sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.grey[500], 0.03),
              }}>
                <Stack spacing={1.5}>
                  {/* Ask AI Button — always visible */}
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => openAiChatForCheck(dc)}
                    startIcon={<AiSparkIcon size={16} />}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.1,
                      background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                      boxShadow: 'none',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)',
                        boxShadow: 'none'
                      }
                    }}
                  >
                    Ask AI About This Finding
                  </Button>

                  {/* Suppress — only for failed checks */}
                  {!isPassed && (
                    isFindingSuppressed(dc.checkType) ? (
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ py: 0.5 }}>
                        <Shield sx={{ fontSize: 16, color: 'warning.main' }} />
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'warning.dark' }}>
                          This finding is currently suppressed
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        fullWidth
                        variant="outlined"
                        color="warning"
                        onClick={() => setShowSuppressDialog(true)}
                        startIcon={<Shield sx={{ fontSize: 16 }} />}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          py: 1,
                          borderColor: alpha(theme.palette.warning.main, 0.4),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.warning.main, 0.06),
                            borderColor: 'warning.main'
                          }
                        }}
                      >
                        Suppress Finding
                      </Button>
                    )
                  )}
                </Stack>
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* Resource Detail Drawer - slides on top of Check Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerResource)}
        onClose={closeResourceDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '70%' },
            borderRadius: '16px 0 0 16px',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
            zIndex: 1301
          }
        }}
        sx={{ zIndex: 1301 }}
      >
        {drawerResource && (() => {
          const res = drawerResource;
          const resourceId = res.resourceId || 'N/A';
          const region = res.region || 'global';
          const check = res.check || {};
          const service = check.service || check.category || 'Unknown';
          const isPassed = res.status && ['PASS', 'PASSED', 'COMPLIANT', 'OK'].includes(res.status.toUpperCase());

          // Parse resource details from resourceId
          const isArn = resourceId.startsWith('arn:');
          let resourceName = resourceId;
          let resourceType = service;
          let arnParts = {};

          if (isArn) {
            const parts = resourceId.split(':');
            arnParts = {
              partition: parts[1] || 'aws',
              service: parts[2] || '',
              region: parts[3] || '',
              accountId: parts[4] || '',
              resource: parts.slice(5).join(':') || ''
            };
            resourceName = arnParts.resource.split('/').pop() || resourceId;
            resourceType = `Aws${(arnParts.service || '').charAt(0).toUpperCase() + (arnParts.service || '').slice(1)}`;
          } else {
            // Try to extract name from common patterns
            resourceName = resourceId.split('/').pop() || resourceId;
          }

          // Map service to resource type
          const serviceTypeMap = {
            'IAM': 'AwsIamUser',
            'S3': 'AwsS3Bucket',
            'EC2': 'AwsEc2Instance',
            'RDS': 'AwsRdsInstance',
            'Lambda': 'AwsLambdaFunction',
            'CloudTrail': 'AwsCloudTrail',
            'VPC': 'AwsVpc',
            'SecurityGroup': 'AwsEc2SecurityGroup',
            'KMS': 'AwsKmsKey',
            'EBS': 'AwsEc2Volume',
            'CloudWatch': 'AwsCloudWatchLogGroup',
            'GuardDuty': 'AwsGuardDutyDetector',
            'Config': 'AwsConfigRecorder',
            'Backup': 'AwsBackupPlan',
            'SSM': 'AwsSsmManagedInstance'
          };
          if (!isArn) {
            resourceType = serviceTypeMap[service] || `Aws${service}Resource`;
          }

          const DetailRow = ({ label, value, mono }) => (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                {label}
              </Typography>
              <Box sx={{
                mt: 0.5,
                p: 1.5,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="body2" sx={{
                  fontFamily: mono ? 'monospace' : 'inherit',
                  fontSize: mono ? '0.78rem' : '0.85rem',
                  wordBreak: 'break-all',
                  color: 'text.primary'
                }}>
                  {value || 'N/A'}
                </Typography>
              </Box>
            </Box>
          );

          const DetailRowPair = ({ label1, value1, label2, value2 }) => (
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                  {label1}
                </Typography>
                <Box sx={{
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                    {value1 || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                  {label2}
                </Typography>
                <Box sx={{
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.primary' }}>
                    {value2 || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          );

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Header */}
              <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box sx={{ flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <IconButton size="small" onClick={closeResourceDrawer} sx={{ mr: 0.5, ml: -1 }}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke={theme.palette.text.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="13 15 8 10 13 5" />
                        </svg>
                      </IconButton>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Resource Details
                      </Typography>
                    </Box>
                    <Chip
                      label={isPassed ? 'COMPLIANT' : 'NON-COMPLIANT'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: isPassed ? 'success.main' : 'error.main',
                        color: 'white',
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                  <IconButton onClick={closeResourceDrawer} size="small" sx={{ mt: -0.5 }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* Scrollable Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                {/* Resource ID */}
                <DetailRow label="Resource ID" value={resourceId} mono />

                {/* Resource Name & Type */}
                <DetailRowPair
                  label1="Resource Name"
                  value1={resourceName}
                  label2="Resource Type"
                  value2={resourceType}
                />

                {/* Service & Region */}
                <DetailRowPair
                  label1="Service"
                  value1={service.toLowerCase()}
                  label2="Region"
                  value2={region}
                />

                {/* Status & Severity */}
                <DetailRowPair
                  label1="Status"
                  value1={res.status || 'UNKNOWN'}
                  label2="Severity"
                  value2={check.severity || 'N/A'}
                />

                {/* Creation Date & Last Scanned */}
                <DetailRowPair
                  label1="Created At"
                  value1={res.createdAt 
                    ? new Date(res.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : 'Not available'}
                  label2="Last Scanned"
                  value2={new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                />

                {/* CLI Command to Fix */}
                {!isPassed && (() => {
                  const cliFixMap = {
                    'IAM': {
                      cmd: `aws iam create-virtual-mfa-device --virtual-mfa-device-name ${resourceName}-mfa --outfile /tmp/${resourceName}-mfa.png --bootstrap-method QRCodePNG\naws iam enable-mfa-device --user-name ${resourceName} --serial-number arn:aws:iam::ACCOUNT_ID:mfa/${resourceName}-mfa --authentication-code-1 CODE1 --authentication-code-2 CODE2`,
                      impact: 'The user will be required to use MFA for all future console logins and API calls that require MFA. Existing sessions will not be affected. Ensure the user has access to the MFA device before enabling.'
                    },
                    'S3': {
                      cmd: resourceId.includes('versioning')
                        ? `aws s3api put-bucket-versioning --bucket ${resourceName} --versioning-configuration Status=Enabled`
                        : resourceId.includes('encryption') || check.checkType?.toLowerCase().includes('encrypt')
                          ? `aws s3api put-bucket-encryption --bucket ${resourceName} --server-side-encryption-configuration '{"Rules":[{"ApplyServerSideEncryptionByDefault":{"SSEAlgorithm":"AES256"}}]}'`
                          : `aws s3api put-public-access-block --bucket ${resourceName} --public-access-block-configuration "BlockPublicAcls=true,IgnorePublicAcls=true,BlockPublicPolicy=true,RestrictPublicBuckets=true"`,
                      impact: check.checkType?.toLowerCase().includes('versioning')
                        ? 'No service disruption. Versioning will apply to new objects. Existing objects are not affected. Storage costs may increase as previous versions are retained.'
                        : check.checkType?.toLowerCase().includes('encrypt')
                          ? 'No service disruption. Encryption applies to new objects only. Existing unencrypted objects remain unchanged. Applications reading objects are not affected.'
                          : 'Applications or users relying on public access to this bucket will lose access immediately. Verify no public-facing services depend on this bucket before applying.'
                    },
                    'EC2': {
                      cmd: resourceId.includes('sg-') || check.checkType?.toLowerCase().includes('security group')
                        ? `aws ec2 revoke-security-group-ingress --group-id ${resourceName} --protocol tcp --port 22 --cidr 0.0.0.0/0\naws ec2 authorize-security-group-ingress --group-id ${resourceName} --protocol tcp --port 22 --cidr YOUR_IP/32`
                        : `aws ec2 modify-instance-attribute --instance-id ${resourceName} --no-source-dest-check`,
                      impact: check.checkType?.toLowerCase().includes('security group')
                        ? 'Revoking open SSH access will immediately block all inbound SSH connections from unrestricted IPs. Ensure you add your trusted IP range before removing the open rule, or you may lose SSH access.'
                        : 'Modifying instance attributes may require a brief network interruption. Running applications should reconnect automatically.'
                    },
                    'VPC': {
                      cmd: `aws ec2 create-flow-logs --resource-type VPC --resource-ids ${resourceName} --traffic-type ALL --log-destination-type cloud-watch-logs --log-group-name /aws/vpc/flowlogs/${resourceName} --deliver-logs-permission-arn arn:aws:iam::ACCOUNT_ID:role/vpc-flow-logs-role`,
                      impact: 'No service disruption. Flow logs capture network traffic metadata only and do not affect network performance. Additional CloudWatch Logs costs will apply based on log volume.'
                    },
                    'CloudTrail': {
                      cmd: `aws cloudtrail create-trail --name ${resourceName}-trail --s3-bucket-name YOUR_BUCKET --is-multi-region-trail\naws cloudtrail start-logging --name ${resourceName}-trail`,
                      impact: 'No service disruption. CloudTrail logging is a passive monitoring service. S3 storage costs will increase based on API activity volume.'
                    },
                    'KMS': {
                      cmd: `aws kms enable-key-rotation --key-id ${resourceName}`,
                      impact: 'No service disruption. Key rotation is automatic and transparent. Applications using the key will continue to work without changes. Previous key versions are retained for decryption.'
                    },
                    'CloudWatch': {
                      cmd: `aws logs associate-kms-key --log-group-name ${resourceName} --kms-key-id arn:aws:kms:REGION:ACCOUNT_ID:key/KEY_ID`,
                      impact: 'No service disruption. Encryption applies to new log events. Existing log data remains accessible. Ensure the KMS key policy allows CloudWatch Logs access.'
                    },
                    'GuardDuty': {
                      cmd: `aws guardduty create-detector --enable`,
                      impact: 'No service disruption. GuardDuty is a passive threat detection service. It may generate findings that require investigation. Costs are based on the volume of AWS CloudTrail events and VPC Flow Logs analyzed.'
                    },
                    'Config': {
                      cmd: `aws configservice put-configuration-recorder --configuration-recorder name=default,roleARN=arn:aws:iam::ACCOUNT_ID:role/aws-config-role --recording-group allSupported=true,includeGlobalResourceTypes=true\naws configservice start-configuration-recorder --configuration-recorder-name default`,
                      impact: 'No service disruption. AWS Config records configuration changes passively. Costs are based on the number of configuration items recorded and rules evaluated.'
                    },
                    'Backup': {
                      cmd: `aws backup create-backup-plan --backup-plan '{"BackupPlanName":"${resourceName}-plan","Rules":[{"RuleName":"DailyBackup","TargetBackupVaultName":"Default","ScheduleExpression":"cron(0 5 ? * * *)","StartWindowMinutes":60,"CompletionWindowMinutes":180,"Lifecycle":{"DeleteAfterDays":30}}]}'`,
                      impact: 'No service disruption. Backups run during the specified window. Storage costs will increase based on backup size and retention period. Initial backup may take longer depending on resource size.'
                    },
                    'RDS': {
                      cmd: check.checkType?.toLowerCase().includes('encrypt')
                        ? `# RDS encryption requires creating an encrypted copy\naws rds create-db-snapshot --db-instance-identifier ${resourceName} --db-snapshot-identifier ${resourceName}-pre-encrypt\naws rds copy-db-snapshot --source-db-snapshot-identifier ${resourceName}-pre-encrypt --target-db-snapshot-identifier ${resourceName}-encrypted --kms-key-id alias/aws/rds\naws rds restore-db-instance-from-db-snapshot --db-instance-identifier ${resourceName}-encrypted --db-snapshot-identifier ${resourceName}-encrypted`
                        : `aws rds modify-db-instance --db-instance-identifier ${resourceName} --multi-az --apply-immediately`,
                      impact: check.checkType?.toLowerCase().includes('encrypt')
                        ? 'Requires creating a new encrypted instance from snapshot. The original instance must be stopped and replaced. Expect 15-30 minutes of downtime. Update application connection strings to point to the new instance.'
                        : 'Enabling Multi-AZ triggers a brief failover (typically 1-2 minutes). Applications with proper connection retry logic will reconnect automatically. Schedule during a maintenance window.'
                    },
                    'Lambda': {
                      cmd: `aws lambda update-function-configuration --function-name ${resourceName} --vpc-config SubnetIds=SUBNET_IDS,SecurityGroupIds=SG_IDS`,
                      impact: 'The Lambda function will be briefly unavailable during the update (typically a few seconds). Cold start times may increase when running inside a VPC. Ensure the VPC has a NAT Gateway if the function needs internet access.'
                    },
                    'SSM': {
                      cmd: `aws ssm create-association --name "AWS-GatherSoftwareInventory" --targets "Key=instanceids,Values=${resourceName}" --schedule-expression "rate(1 day)"`,
                      impact: 'No service disruption. SSM inventory collection runs as a background task on the instance. Minimal CPU and memory overhead.'
                    }
                  };

                  const fix = cliFixMap[service] || {
                    cmd: `# Review the check "${check.checkType || 'Unknown'}" and apply the appropriate fix for resource: ${resourceName}`,
                    impact: 'Review the remediation steps in the previous panel and assess the impact before applying changes. Test in a non-production environment first.'
                  };

                  return (
                    <>
                      {/* CLI Fix Command */}
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                          CLI Command to Fix
                        </Typography>
                        <Box sx={{
                          mt: 0.5,
                          p: 2,
                          borderRadius: 1.5,
                          bgcolor: theme.palette.mode === 'dark' ? '#1a1a2e' : '#1e1e1e',
                          border: '1px solid',
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.2)',
                          position: 'relative'
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(fix.cmd);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              color: 'rgba(255,255,255,0.5)',
                              '&:hover': { color: 'rgba(255,255,255,0.8)' }
                            }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                          <Typography
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              color: '#e0e0e0',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-all',
                              m: 0,
                              pr: 4,
                              lineHeight: 1.8
                            }}
                          >
                            {fix.cmd}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Impact Note */}
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                          Impact Assessment
                        </Typography>
                        <Box sx={{
                          mt: 0.5,
                          p: 2,
                          borderRadius: 1.5,
                          bgcolor: alpha(theme.palette.warning.main, 0.06),
                          border: '1px solid',
                          borderColor: alpha(theme.palette.warning.main, 0.25),
                          display: 'flex',
                          gap: 1.5,
                          alignItems: 'flex-start'
                        }}>
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(theme.palette.warning.main, 0.15),
                            mt: 0.25
                          }}>
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke={theme.palette.warning.dark} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M10 4v6M10 14v.01" />
                            </svg>
                          </Box>
                          <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.primary' }}>
                            {fix.impact}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Box>
          );
        })()}
      </Drawer>

      {/* AWS Account Setup Dialog */}
      <Dialog 
        open={showAWSSetup} 
        onClose={() => setShowAWSSetup(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <AWSAccountSetup 
          onClose={() => setShowAWSSetup(false)}
          onAccountAdded={handleAccountAdded}
        />
      </Dialog>

      {/* Debug Info Dialog */}
      <Dialog 
        open={showDebugInfo} 
        onClose={() => setShowDebugInfo(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Terminal color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              System Diagnostics
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {debugInfo && (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {(debugInfo.checks || []).map((check, i) => (
                <Alert
                  key={i}
                  severity={check.status === 'success' ? 'success' : check.status === 'warning' ? 'warning' : 'error'}
                >
                  <Typography variant="body2">
                    <strong>{check.name}:</strong> {check.message}
                  </Typography>
                </Alert>
              ))}
              {(!debugInfo.checks || debugInfo.checks.length === 0) && (
                <Alert severity="info">No diagnostic data available.</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setShowDebugInfo(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Scan History Dialog */}
      <Dialog 
        open={showScanHistory} 
        onClose={() => setShowScanHistory(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <History />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Scan History
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {scanHistory.length} scan{scanHistory.length !== 1 ? 's' : ''} recorded
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {scanHistory.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <History sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Scan History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run your first security scan to start tracking history
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {scanHistory.map((scan, index) => (
                <React.Fragment key={scan.id}>
                  <ListItem
                    button
                    onClick={() => loadScanFromHistory(scan)}
                    sx={{
                      py: 2.5,
                      px: 3,
                      '&:hover': { bgcolor: 'action.hover' },
                      borderLeft: index === 0 ? '4px solid' : 'none',
                      borderColor: 'primary.main'
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: index === 0 ? 'primary.main' : 'grey.200', width: 48, height: 48 }}>
                        <Assessment />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                          {/* Account name + AWS account ID — always shown */}
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {scan.account_name || scan.accountName || 'Unknown Account'}
                          </Typography>
                          {(scan.aws_account_id || scan.awsAccountId) && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', px: 0.75, py: 0.25, borderRadius: 1 }}>
                              {scan.aws_account_id || scan.awsAccountId}
                            </Typography>
                          )}
                          {index === 0 && (
                            <Chip label="Latest" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                          {scan.scan_type === 'scheduled' && (
                            <Chip label="Scheduled" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatTimestamp(scan.completed_at || scan.timestamp)}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                              ({formatFullTimestamp(scan.completed_at || scan.timestamp)})
                            </Typography>
                          </Box>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            <Chip
                              label={`Score: ${scan.risk_score ?? scan.summary?.score ?? 0}%`}
                              size="small"
                              sx={{
                                height: 24, fontSize: '0.75rem', fontWeight: 600, color: 'white',
                                bgcolor: (scan.risk_score ?? scan.summary?.score ?? 0) >= 80 ? '#4caf50'
                                  : (scan.risk_score ?? scan.summary?.score ?? 0) >= 60 ? '#ff9800' : '#f44336'
                              }}
                            />
                            <Chip label={`${scan.passed ?? scan.summary?.passed ?? 0} Passed`} size="small" sx={{ height: 24, fontSize: '0.75rem', bgcolor: '#4caf50', color: 'white', fontWeight: 600 }} />
                            <Chip label={`${scan.failed ?? scan.summary?.failed ?? 0} Failed`} size="small" sx={{ height: 24, fontSize: '0.75rem', bgcolor: '#f44336', color: 'white', fontWeight: 600 }} />
                            {scan.duration_ms && (
                              <Chip label={`${Math.round(scan.duration_ms / 1000)}s`} size="small" variant="outlined" sx={{ height: 24, fontSize: '0.75rem' }} />
                            )}
                            {scan.status === 'failed' && (
                              <Chip label="Failed" size="small" sx={{ height: 24, fontSize: '0.75rem', bgcolor: '#f44336', color: 'white', fontWeight: 600 }} />
                            )}
                          </Box>
                        </Stack>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Download Excel Report">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const token = localStorage.getItem('token');
                            const accId = scan.db_account_id || (scan.accountId || selectedAccount);
                            const accName = (scan.account_name || scan.accountName || 'account').replace(/\s+/g, '-');
                            const dateStr = new Date(scan.completed_at || scan.timestamp).toISOString().split('T')[0];
                            fetch(`/api/security/reports/${accId}/${scan.id}/xlsx`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            })
                            .then(r => r.ok ? r.blob() : r.json().then(e => Promise.reject(e.error || 'Failed')))
                            .then(blob => {
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = `cspm-${accName}-${dateStr}.xlsx`;
                              document.body.appendChild(a); a.click();
                              window.URL.revokeObjectURL(url); document.body.removeChild(a);
                              addNotification({ type: 'success', title: 'Downloaded', message: `Excel report for ${scan.account_name || scan.accountName}` });
                            })
                            .catch(err => addNotification({ type: 'error', title: 'Download Failed', message: typeof err === 'string' ? err : 'Unable to download' }));
                          }}
                          sx={{ color: '#1D6F42' }}
                        >
                          <FileDownload />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Load this scan">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            loadScanFromHistory(scan);
                          }}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < scanHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Button onClick={() => setShowScanHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* AI Security Chat Drawer */}
      <Drawer
        anchor="right"
        open={showAiChat}
        onClose={() => setShowAiChat(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 480 },
            borderRadius: '16px 0 0 16px',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* Chat Header */}
        <Box sx={{
          p: 2.5, borderBottom: '1px solid', borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(124,58,237,0.06) 0%, rgba(37,99,235,0.06) 100%)'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AiSparkIcon size={18} color="white" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Astra Security
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {aiChatCheckContext ? aiChatCheckContext.checkType : 'Ask about your CSPM findings'}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setShowAiChat(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          {aiChatCheckContext && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.12)' }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  label={aiChatCheckContext.severity || 'N/A'}
                  size="small"
                  sx={{
                    height: 20, fontSize: '0.65rem', fontWeight: 700,
                    bgcolor: aiChatCheckContext.severity === 'HIGH' || aiChatCheckContext.severity === 'CRITICAL' ? 'error.main'
                      : aiChatCheckContext.severity === 'MEDIUM' ? 'warning.main' : 'info.main',
                    color: 'white'
                  }}
                />
                {aiChatCheckContext.service && (
                  <Chip label={aiChatCheckContext.service} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {aiChatCheckContext.resourceCount} resource{aiChatCheckContext.resourceCount !== 1 ? 's' : ''} affected
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Chat Messages */}
        <Box sx={{
          flex: 1, overflow: 'auto', p: 2.5,
          display: 'flex', flexDirection: 'column', gap: 2,
          bgcolor: alpha(theme.palette.grey[500], 0.02)
        }}>
          {aiChatMessages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%', mx: 'auto', mb: 2,
                background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(37,99,235,0.1) 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AiSparkIcon size={24} color="#7c3aed" />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {aiChatCheckContext
                  ? `Ask me about "${aiChatCheckContext.checkType}"`
                  : 'How can I help with your security posture?'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                {aiChatCheckContext
                  ? 'I have full context of this specific finding, its remediation, and affected resources.'
                  : 'I have full context of your CSPM scan results.'}
              </Typography>
              <Stack spacing={1}>
                {(aiChatCheckContext ? [
                  `Explain the risk of "${aiChatCheckContext.checkType}" in simple terms`,
                  'What is the step-by-step fix with zero downtime?',
                  'Could this be exploited by an attacker? How?',
                  'What compliance frameworks does this violate?',
                  'Is it safe to suppress this finding?'
                ] : [
                  'What are the most critical risks I should fix first?',
                  'Which findings could lead to a data breach?',
                  'How do I fix the open security group issues?',
                  'What is my SOC 2 compliance gap?'
                ]).map((suggestion, idx) => (
                  <Box
                    key={idx}
                    onClick={() => { setAiChatInput(suggestion); }}
                    sx={{
                      p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: '#7c3aed', bgcolor: 'rgba(124,58,237,0.03)' }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>{suggestion}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          {aiChatMessages.map((msg, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', gap: 1 }}>
              {msg.role === 'assistant' && (
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0, mt: 0.5,
                  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <AiSparkIcon size={14} color="white" />
                </Box>
              )}
              <Box sx={{
                maxWidth: '85%', p: 2, borderRadius: 2.5,
                ...(msg.role === 'user'
                  ? { bgcolor: 'primary.main', color: 'white', borderBottomRightRadius: 4 }
                  : { bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderBottomLeftRadius: 4 })
              }}>
                {msg.role === 'user' ? (
                  <Typography variant="body2" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                ) : (
                  <Box sx={{
                    '& p': { m: 0, mb: 1, lineHeight: 1.7, fontSize: '0.875rem', '&:last-child': { mb: 0 } },
                    '& ul, & ol': { m: 0, mb: 1, pl: 2.5, '&:last-child': { mb: 0 } },
                    '& li': { mb: 0.5, fontSize: '0.875rem', lineHeight: 1.6 },
                    '& strong': { fontWeight: 600 },
                    '& code': { bgcolor: 'grey.100', color: 'text.primary', px: 0.75, py: 0.25, borderRadius: 0.5, fontSize: '0.8rem', fontFamily: 'monospace' },
                    '& pre': { bgcolor: '#1e1e1e', color: '#e0e0e0', p: 1.5, borderRadius: 1.5, overflow: 'auto', mb: 1, '& code': { bgcolor: 'transparent', color: 'inherit', p: 0 } },
                  }}>
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
          {aiChatLoading && (
            <Box display="flex" alignItems="center" gap={1}>
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <AiSparkIcon size={14} color="white" />
              </Box>
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" gap={0.5}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 6, height: 6, borderRadius: '50%', bgcolor: '#7c3aed',
                      animation: 'pulse 1.4s infinite', animationDelay: `${i * 0.2}s`,
                      '@keyframes pulse': { '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' }, '40%': { opacity: 1, transform: 'scale(1)' } }
                    }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* Chat Input */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth size="small"
              placeholder="Ask about your security findings..."
              value={aiChatInput}
              onChange={(e) => setAiChatInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAiChatSend(); } }}
              disabled={aiChatLoading}
              multiline maxRows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '0.875rem' } }}
            />
            <IconButton
              onClick={handleAiChatSend}
              disabled={!aiChatInput.trim() || aiChatLoading}
              sx={{
                width: 40, height: 40, borderRadius: 2,
                background: aiChatInput.trim() ? 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)' : 'transparent',
                color: aiChatInput.trim() ? 'white' : 'text.disabled',
                '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)' },
                '&.Mui-disabled': { background: 'transparent' }
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Drawer>

      {/* Suppress Finding Dialog */}
      <Dialog
        open={showSuppressDialog}
        onClose={() => setShowSuppressDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              bgcolor: alpha('#ed6c02', 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Shield sx={{ fontSize: 20, color: 'warning.main' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }}>
                Suppress Finding
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {drawerCheck?.checkType}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Reason for suppression
              </Typography>
              <RadioGroup
                value={suppressReason}
                onChange={(e) => setSuppressReason(e.target.value)}
              >
                {Object.entries(reasonLabels).map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {value === 'accepted_risk' && 'The risk has been reviewed and accepted by the team'}
                          {value === 'false_positive' && 'This finding does not apply to the current configuration'}
                          {value === 'compensating_control' && 'An alternative control mitigates this risk'}
                          {value === 'not_applicable' && 'This check is not relevant to this environment'}
                        </Typography>
                      </Box>
                    }
                    sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { mt: -0.25 } }}
                  />
                ))}
              </RadioGroup>
            </Box>

            <TextField
              label="Justification"
              placeholder="Provide context for why this finding is being suppressed..."
              multiline
              rows={3}
              value={suppressJustification}
              onChange={(e) => setSuppressJustification(e.target.value)}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            <TextField
              label="Expiry Date (optional)"
              type="date"
              value={suppressExpiry}
              onChange={(e) => setSuppressExpiry(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for permanent suppression"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => {
              setShowSuppressDialog(false);
              setSuppressReason('accepted_risk');
              setSuppressJustification('');
              setSuppressExpiry('');
            }}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSuppressSubmit}
            startIcon={<Shield sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            Suppress Finding
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Security;
